import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  MicrophoneIcon,
  ChevronDownIcon,
  StopIcon,
  DocumentIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { FiGlobe } from "react-icons/fi";
import { LuRefreshCcw, LuSparkles, LuTelescope } from "react-icons/lu";
import { FaArrowUp, FaSuperpowers, FaRegImage } from "react-icons/fa6";
import { FaGithub } from "react-icons/fa";
import { SiNotion } from "react-icons/si";
import axios from 'axios';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import FileContextDisplay from './FileContextDisplay';
import GoogleDriveModal from './GoogleDriveModal';
import ChatSuggestions from './ChatSuggestions';
import ConnectYourToolsBar from './ConnectYourToolsBar';
import ConnectToolsModal from '../../../components/layout/ConnectToolsModal';
import ChatQuickActions from './ChatQuickActions';
import { toast } from 'react-hot-toast';
import { useVisionModelFallback } from '../../../hooks/useVisionModelFallback';
import { useSpellCheck } from '../../../hooks/useSpellCheck';
import { ReactComponent as BMLogoBlack } from '../../../assets/nvgtr_BM.svg';
import { ReactComponent as BMLogoWhite } from '../../../assets/nvgtr_BM_white.svg';
import { useFullLogo } from '../../../hooks/useFullLogo';
import { ReactComponent as OpenAILogo } from '../../../assets/openai.svg';
import { ReactComponent as ClaudeLogo } from '../../../assets/claude.svg';
import { ReactComponent as GeminiLogo } from '../../../assets/gemini.svg';
import { TbWorldSearch as SearchIcon } from "react-icons/tb";
import { useTheme } from '../../../features/theme/context/ThemeContext';
import gmailSvg from '../../../assets/gmail.svg';
import googleCalendarSvg from '../../../assets/google-calendar.svg';
import driveSvg from '../../../assets/drive.svg';
import outlookSvg from '../../../assets/outlook.svg';
import outlookCalendarSvg from '../../../assets/outlook-calendar.svg';
import sharepointSvg from '../../../assets/sharepoint.svg';
import onedriveSvg from '../../../assets/onedrive.svg';
import { TbPlug } from 'react-icons/tb';
import {
  getCornerShapeSquircleInlineStyle,
  getPlainRadiusForContinuousCornerDesignPx
} from '../../../utils/squircleCompat';
import { getUserColor as getProjectUserColor } from '../../project/utils/helpers';
// Removed FiDatabase import - using FaSuperpowers instead
import { isOrganizationAdmin } from '../../../utils/planPermissions'; // Import role checking utility
import UsageLimitNotification from './UsageLimitNotification';
import useUsageNotifications from '../../../hooks/useUsageNotifications';
import PaymentFailureNotification from './PaymentFailureNotification';
import usePaymentNotifications from '../../../hooks/usePaymentNotifications';
import SystemStatusNotification from './SystemStatusNotification';
import useSystemStatus from '../../../hooks/useSystemStatus';
import { transcribeAudioRecording } from '../../../services/transcription';
import { normalizeImageUploads } from '../../../utils/normalizeImageUploads';
import {
  resolveInitialChatModelSelection,
  resolveModelSelectionForNewChat,
  buildMidusPickerRows,
} from '../../../utils/defaultChatModel';

const STORAGE_KEY = 'chat_input_state';
const MIN_TRANSCRIPTION_DURATION_MS = 1500;
const TRANSCRIPTION_WARNING_MESSAGE = "Midus didn't quite catch that. Please try again.";
const METERING_INTERVAL_MS = 120;
const DB_NORMALIZATION_OFFSET = 40;
const AMPLITUDE_SCALE = 0.06;
const BAR_WIDTH = 1.5;
const BAR_SPACING = 1;
const BAR_SLOT = BAR_WIDTH + BAR_SPACING;
const INITIAL_LEVEL = -DB_NORMALIZATION_OFFSET;
const MAX_LEVEL_HISTORY = 160; // Match SAMPLE_WAVEFORM_LEVELS so live waveform fills same width as preview
const TRANSCRIPTION_HALLUCINATION_PATTERNS = [
  /^(?:thanks|thank you)(?: so much)? for watching(?: (?:thanks|thank you)(?: so much)? for watching)*$/,
  /^(?:thanks|thank you)(?: so much)?$/,
];
// Force-show waveform + timer for design: add ?preview=voice to URL
const FORCE_SHOW_RECORDING_UI = typeof window !== 'undefined' && window.location.search.includes('preview=voice');
// Sample levels for design preview (dB values: -40 = silence, 0 = loud)
const SAMPLE_WAVEFORM_LEVELS = Array.from({ length: 160 }, (_, i) => {
  const t = i / 160;
  return -40 + 35 * (0.3 + 0.7 * Math.sin(t * Math.PI * 4) * Math.sin(t * Math.PI * 2));
});
const GENERAL_MENTION_REGEX = /@[A-Za-z0-9._-]+/g;
const escapeHtml = (unsafe = '') => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Logo component that uses currentColor to match text
const BMLogoCurrentColor = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 558 268" 
    fill="currentColor"
    {...props}
  >
    <path d="M253.902 0.172614C198.292 4.33261 163.672 92.9626 139.282 135.543C121.792 166.073 89.2124 227.253 47.7624 224.773C35.8124 224.063 29.0124 215.113 15.7024 216.953C7.90242 218.033 0.60242 226.653 0.0524198 234.323C-1.38758 254.593 27.1824 265.423 43.6324 266.843C140.032 275.203 184.832 128.833 228.302 66.5426C244.452 43.4026 269.312 33.7126 275.842 69.6326C285.462 122.473 268.722 229.903 331.132 256.343C375.262 275.033 412.082 232.943 434.932 201.183C442.412 190.793 466.812 157.993 469.832 148.083C474.522 132.703 464.622 118.833 448.052 122.303C439.522 124.093 432.862 132.583 427.712 138.963C408.662 162.553 351.522 268.483 329.592 186.873C319.462 149.163 323.562 106.983 318.982 68.4826C314.812 33.5626 293.822 -2.81739 253.902 0.172614Z" />
    <circle cx="514.5" cy="43.5" r="43.5" />
  </svg>
);

// HubSpot icon (matches Connect Tools modal - brand colors)
const HubSpotIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 489 511.8" className="w-full h-full" {...props}>
    <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z" />
  </svg>
);

// Replace infinity symbol with actual SVG icon
const InfinityIcon = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" />
  </svg>
);

// Custom Plus icon - 16x16 viewBox, slightly inset for balance
const ChatInputPlusIcon = (props) => (
  <svg 
    viewBox="0 0 16 16" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={1.5}
    strokeLinecap="round" 
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg" 
    preserveAspectRatio="xMidYMid meet"
    style={{ display: 'block', width: '100%', height: '100%' }}
    {...props}
  >
    <path d="M8 2.5v11M2.5 8h11" />
  </svg>
);

// Custom Tools icon - viewBox slightly tighter than original for better fill
const ToolsIcon = (props) => (
  <svg 
    viewBox="1 1 22 22" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg" 
    preserveAspectRatio="xMidYMid meet"
    style={{ display: 'block', width: '100%', height: '100%' }}
    {...props}
  >
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M14.5 5C13.3954 5 12.5 5.89543 12.5 7C12.5 8.10457 13.3954 9 14.5 9C15.6046 9 16.5 8.10457 16.5 7C16.5 5.89543 15.6046 5 14.5 5ZM10.626 6C11.0701 4.27477 12.6362 3 14.5 3C16.3638 3 17.9299 4.27477 18.374 6H20C20.5523 6 21 6.44772 21 7C21 7.55228 20.5523 8 20 8H18.374C17.9299 9.72523 16.3638 11 14.5 11C12.6362 11 11.0701 9.72523 10.626 8H4C3.44772 8 3 7.55228 3 7C3 6.44772 3.44772 6 4 6H10.626ZM9.5 15C8.39543 15 7.5 15.8954 7.5 17C7.5 18.1046 8.39543 19 9.5 19C10.6046 19 11.5 18.1046 11.5 17C11.5 15.8954 10.6046 15 9.5 15ZM5.62602 16C6.07006 14.2748 7.63616 13 9.5 13C11.3638 13 12.9299 14.2748 13.374 16H20C20.5523 16 21 16.4477 21 17C21 17.5523 20.5523 18 20 18H13.374C12.9299 19.7252 11.3638 21 9.5 21C7.63616 21 6.07006 19.7252 5.62602 18H4C3.44772 18 3 17.5523 3 17C3 16.4477 3.44772 16 4 16H5.62602Z" 
      fill="currentColor"
    />
  </svg>
);



// Define a big infinity symbol constant for consistent use - no longer needed
// const BIG_INFINITY = '∞'; // Unicode infinity symbol

const formatDuration = (ms) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const WAVEFORM_VIEWBOX_WIDTH = 400;
const WAVEFORM_VIEWBOX_HEIGHT = 28;

const VoiceWaveform = ({ height, color, levels }) => {
  const halfHeight = height / 2;
  const maxBars = Math.max(1, Math.floor(WAVEFORM_VIEWBOX_WIDTH / BAR_SLOT));
  const visibleLevels = levels.slice(-maxBars);

  if (!visibleLevels.length) {
    return (
      <svg viewBox={`0 0 ${WAVEFORM_VIEWBOX_WIDTH} ${height}`} preserveAspectRatio="none" className="block w-full h-full min-h-[28px]">
        <rect x={0} y={halfHeight - 1} width={WAVEFORM_VIEWBOX_WIDTH} height={2} fill={color} opacity={0.25} rx={1} />
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${WAVEFORM_VIEWBOX_WIDTH} ${height}`} preserveAspectRatio="none" className="block w-full h-full min-h-[28px]">
      {visibleLevels.map((level, index) => {
        const normalized = Math.max(0, Math.pow(10, (level + DB_NORMALIZATION_OFFSET) / 20));
        const clamped = Math.min(1, normalized * AMPLITUDE_SCALE);
        const barHeight = Math.max(2, clamped * height);
        const y = halfHeight - barHeight / 2;
        const x = index * BAR_SLOT;
        return (
          <rect
            key={`${index}-${level}-${visibleLevels.length}`}
            x={x}
            y={y}
            width={BAR_WIDTH}
            height={barHeight}
            rx={BAR_WIDTH / 2}
            fill={color}
          />
        );
      })}
    </svg>
  );
};

const VoiceRecordingIndicator = ({ durationLabel, levels, darkMode }) => {
  const timerBg = darkMode ? 'rgba(245,245,245,0.08)' : 'rgba(17,17,17,0.05)';
  const timerBorder = darkMode ? 'rgba(245,245,245,0.25)' : 'rgba(17,17,17,0.1)';
  const labelColor = darkMode ? '#f5f5f5' : '#111111';
  const waveColor = darkMode ? '#999999' : '#6b7280';
  // Must match chat shell: `bg-[#F9FAFB] dark:bg-[#181818]` on the input wrapper (not #f2f2f2).
  const containerBg = darkMode ? '#181818' : '#F9FAFB';
  const containerBgTransparent = darkMode ? 'rgba(24,24,24,0)' : 'rgba(249,250,251,0)';

  return (
    <div className="flex-1 min-w-0 w-full min-h-[32px] flex items-center gap-2">
      <div
        className="min-w-[64px] shrink-0 h-8 rounded-xl border flex items-center justify-center"
        style={{ backgroundColor: timerBg, borderColor: timerBorder }}
      >
        <span className="text-[13px] font-semibold tabular-nums" style={{ color: labelColor }}>
          {durationLabel}
        </span>
      </div>
      <div className="flex-1 min-w-0 w-full h-7 flex items-end overflow-hidden relative">
        <VoiceWaveform height={28} color={waveColor} levels={levels} />
        {/* Left fade gradient - z-0 so waveform/tooltips appear above */}
        <div
          className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-0"
          style={{
            background: `linear-gradient(to right, ${containerBg}, ${containerBgTransparent})`,
          }}
        />
        {/* Right fade gradient - z-0 so waveform/tooltips appear above */}
        <div
          className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-0"
          style={{
            background: `linear-gradient(to left, ${containerBg}, ${containerBgTransparent})`,
          }}
        />
      </div>
    </div>
  );
};

const deriveMentionHandle = (member, fallback = 'member') => {
  if (!member) return fallback;
  const handleSource = member.username
    || member.handle
    || member.name
    || member.fullName
    || member.displayName
    || member.email
    || member.phoneNumber
    || fallback;
  return handleSource.replace(/\s+/g, '');
};

const createMentionState = () => ({ active: false, query: '', startIndex: null });

const ChatInput = ({ input, setInput, handleSend, onOptimisticSend = null, handleDiscussionSend = null, disabled, isInitialChatLoad = false, companyName = "your company", organizationNickname, handleFileUpload, onModelSelect, cancelResponse, onHeightChange, error: externalError, onErrorRetry, isOtherUserTyping = false, typingUserColor = null, typingUserName = null, isProjectChat = false, hasMessages = false, user = null, setUser = null, chatId = null, projectId = null, projectData = null, selectedModelFromParent = null }) => {
  console.log('ChatInput component rendered', { 
    chatId, 
    hasMessages, 
    pathname: window.location.pathname,
    isProjectChat,
    input: input?.length || 0
  });
  console.log('ChatInput refs initialized');
  
  const { darkMode } = useTheme();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const modelDropdownRef = useRef(null);
  const toolsDropdownRef = useRef(null);
  const connectorsContainerRef = useRef(null);
  const connectorsDropdownRef = useRef(null);
  const modelButtonRef = useRef(null);
  const toolsButtonRef = useRef(null);
  const chatInputRef = useRef(null);
  const outerContainerRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceTranscribing, setVoiceTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingStartRef = useRef(null);
  const voiceTranscribeControllerRef = useRef(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [levels, setLevels] = useState(() => Array(MAX_LEVEL_HISTORY).fill(INITIAL_LEVEL));
  const meteringIntervalRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isConnectorsDropdownOpen, setIsConnectorsDropdownOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileDataUrls, setFileDataUrls] = useState([]);
  const [fileContext, setFileContext] = useState('');
  const [hyperAccuracy, setHyperAccuracy] = useState(false);
  
  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const isFetchingRef = useRef(false);
  const suggestionsEnabled = false;
  
  // SuperBucket state and admin detection
  // Start false - only turn on when user selects it or API confirms we're in a superbucket chat
  const [isSuperBucketMode, setIsSuperBucketMode] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  
  // Basic mount test
  useEffect(() => {
      // console.log('COMPONENT MOUNTED - Basic useEffect working!');
  // console.log('Current suggestions state:', { 
  //   suggestionsLength: suggestions.length, 
  //   suggestionsVisible, 
  //   loadingSuggestions 
  // });
  }, []);
  
  // Clear suggestions immediately on mount if we're in an existing chat
  useEffect(() => {
    const isInExistingChat = chatId || hasMessages || window.location.pathname.includes('/chat/');
    // console.log('Clear suggestions effect:', { 
    //   chatId, 
    //   hasMessages, 
    //   pathname: window.location.pathname,
    //   isInExistingChat 
    // });
    
    if (isInExistingChat) {
      setSuggestionsVisible(false);
      setSuggestions([]);
    }
  }, [chatId, hasMessages]);


  
  // Add state for models and selection
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(''); // This is for model loading errors
  // Persisted in DB - never cleared unless user explicitly toggles off
  const [userEnabledTools, setUserEnabledTools] = useState([]);
  // Display: superbucket overrides when in superbucket chat, else userEnabledTools
  const selectedTools = useMemo(
    () => (isSuperBucketMode ? ['superbucket'] : userEnabledTools),
    [isSuperBucketMode, userEnabledTools]
  );

  // Google Drive modal state
  const [isGoogleDriveModalOpen, setIsGoogleDriveModalOpen] = useState(false);

  // Connect tools modal - shown when user selects Gmail/Drive/Calendar but hasn't connected
  const [showConnectToolsModal, setShowConnectToolsModal] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('modal') === 'account' && params.get('tab') === 'connectors') return false;
      if (params.get('connectTools') === '1') return true;
      return localStorage.getItem('connectToolsModalOpen') === '1';
    } catch {
      return false;
    }
  });
  // Connected connectors status - which apps user has connected (for connectors dropdown)
  const [connectedConnectors, setConnectedConnectors] = useState({});
  const [isConnectorsLoading, setIsConnectorsLoading] = useState(false);
  const [hasConnectorsBeenFetched, setHasConnectorsBeenFetched] = useState(false);

  // Add drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [containerHeight, setContainerHeight] = useState(80); // Track container height for dynamic radius

  const { checkVisionFallback, fallbackInfo, isChecking, clearFallbackInfo } = useVisionModelFallback();

  // Spell check settings (will be configurable via settings later)
  const spellCheckSettings = {
    spellCheckEnabled: true,
    autoCorrectEnabled: true,
    browserSpellCheckEnabled: true,
    spellCheckAggressiveness: 'medium'
  };

  // Add spell check functionality
  const spellCheck = useSpellCheck({
    enabled: spellCheckSettings.spellCheckEnabled,
    autoCorrectEnabled: spellCheckSettings.autoCorrectEnabled,
    browserSpellCheckEnabled: spellCheckSettings.browserSpellCheckEnabled,
    aggressiveness: spellCheckSettings.spellCheckAggressiveness
  });

  // Add state for user typing tracking
  const [isUserTyping, setIsUserTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const TYPING_TIMEOUT = 60000; // 1 minute timeout for inactivity

  const hasMidusMention = useMemo(() => /@midus\b/i.test(input), [input]);

  // Add state for textarea focus to control SuperBucket aura animation
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  
  // Add state for prompt improvement
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const [lastImprovedPrompt, setLastImprovedPrompt] = useState(null);
  const [isTextareaClearing, setIsTextareaClearing] = useState(false);
  const [isTextareaRevealing, setIsTextareaRevealing] = useState(false);
  const [showImprovingShimmer, setShowImprovingShimmer] = useState(false);
  
  // Add state for scrollbar visibility
  const [showScrollbar, setShowScrollbar] = useState(false);
  
  // Add state for improve prompt button animation
  const [isImproveBtnAnimating, setIsImproveBtnAnimating] = useState(false);
  const [shouldRenderImproveBtn, setShouldRenderImproveBtn] = useState(false);
  
  // Add state for tool indicator animation
  
  // Add state for model selector animation when tools are selected
  const [isModelSelectorAnimating, setIsModelSelectorAnimating] = useState(false);
  const [shouldRenderModelSelector, setShouldRenderModelSelector] = useState(true);

  const projectMembers = useMemo(
    () => (Array.isArray(projectData?.members) ? projectData.members : []),
    [projectData]
  );
  const fullLogoUrls = useFullLogo();

  const userMentionColorHex = useMemo(() => {
    if (user?.userColor) return user.userColor;
    if (user?.id) {
      const colorData = getProjectUserColor(user.id);
      if (typeof colorData === 'string') return colorData;
      if (colorData?.hex) return colorData.hex;
    }
    return darkMode ? '#83C4B4' : '#2C7A7B';
  }, [user?.userColor, user?.id, darkMode]);
  const [mentionState, setMentionState] = useState(createMentionState);
  const [mentionHighlightedIndex, setMentionHighlightedIndex] = useState(0);
  const [mentionCaretIndex, setMentionCaretIndex] = useState(null);
  const mentionableMembers = useMemo(() => {
    if (!isProjectChat || projectMembers.length === 0) return [];
    return projectMembers
      .filter(member => {
        const memberId = member.id || member._id || member.userId;
        const currentUserId = user?.id || user?._id;
        return memberId && currentUserId ? memberId !== currentUserId : true;
      })
      .map((member, index) => {
      const id = member.id || member._id || member.userId || member.phoneNumber || member.email || `member-${index}`;
      const name = member.name || member.fullName || member.displayName || member.username || member.email || 'Member';
      const mentionHandle = deriveMentionHandle(member, `member${index}`);
      const colorSource = member.userColor
        ? { hex: member.userColor }
        : getProjectUserColor(member.id || member._id || member.userId || mentionHandle);
      const colorHex = typeof colorSource === 'string' ? colorSource : colorSource?.hex;
      const avatarUrl = member.profileImage?.url || member.profileImage?.presignedUrl || member.profileImage || null;
      const title = member.role || member.title || member.position || member.jobTitle || '';
      return { id, name, mentionHandle, colorHex, avatarUrl, title };
    });
  }, [projectMembers, isProjectChat, user?.id, user?._id]);
  const mentionColorMap = useMemo(() => {
    const map = {};
    mentionableMembers.forEach(member => {
      if (!member?.mentionHandle) return;
      const key = `@${member.mentionHandle.toLowerCase()}`;
      if (member.colorHex) {
        map[key] = member.colorHex;
      }
    });
    if (userMentionColorHex) {
      map['@midus'] = userMentionColorHex;
    }
    return map;
  }, [mentionableMembers, userMentionColorHex]);
  const filteredMembers = useMemo(() => {
    if (!mentionState.active) return [];
    if (!mentionState.query) return mentionableMembers;
    const queryLower = mentionState.query.toLowerCase();
    return mentionableMembers.filter(member => 
      member.name?.toLowerCase().includes(queryLower) ||
      member.mentionHandle?.toLowerCase().includes(queryLower)
    );
  }, [mentionState.active, mentionState.query, mentionableMembers]);
  const mentionOptionsCount = mentionState.active ? filteredMembers.length + 1 : 0;
  const selectedMentionOption = useMemo(() => {
    if (!mentionState.active) return null;
    if (mentionHighlightedIndex === 0) {
      return {
        mentionHandle: 'midus',
        colorHex: userMentionColorHex,
        displayName: `${organizationNickname || companyName || 'Company'} AI`
      };
    }
    return filteredMembers[mentionHighlightedIndex - 1] || filteredMembers[0] || null;
  }, [mentionState.active, mentionHighlightedIndex, filteredMembers, userMentionColorHex, organizationNickname, companyName]);
  const mentionSuffixColor = useMemo(() => darkMode ? '#5a5a5a' : '#bcbcbc', [darkMode]);
  const selectedMentionHighlightColor = useMemo(() => {
    if (!selectedMentionOption) return null;
    if (selectedMentionOption.mentionHandle?.toLowerCase() === 'midus') {
      return userMentionColorHex;
    }
    return selectedMentionOption.colorHex || null;
  }, [selectedMentionOption, userMentionColorHex]);
  const mentionCompletionSuffix = useMemo(() => {
    if (!mentionState.active || mentionState.startIndex == null || !selectedMentionOption?.mentionHandle) {
      return '';
    }
    const caretPosition = mentionCaretIndex ?? (textareaRef.current ? textareaRef.current.selectionStart : input.length);
    if (caretPosition < mentionState.startIndex) {
      return '';
    }
    const typedSegment = input.slice(mentionState.startIndex, caretPosition);
    const typedWithoutAt = typedSegment.startsWith('@') ? typedSegment.slice(1) : typedSegment;
    if (!typedWithoutAt) return '';
    const handle = selectedMentionOption.mentionHandle;
    const handleLower = handle.toLowerCase();
    const typedLower = typedWithoutAt.toLowerCase();
    if (handleLower.startsWith(typedLower) && handle.length > typedWithoutAt.length) {
      return handle.slice(typedWithoutAt.length);
    }
    return '';
  }, [mentionState.active, mentionState.startIndex, selectedMentionOption?.mentionHandle, mentionCaretIndex, input]);
  const previousMentionQueryRef = useRef('');
  useEffect(() => {
    if (!mentionState.active) {
      previousMentionQueryRef.current = '';
      if (mentionHighlightedIndex !== 0) {
        setMentionHighlightedIndex(0);
      }
      return;
    }

    if (mentionState.query !== previousMentionQueryRef.current) {
      previousMentionQueryRef.current = mentionState.query;

      if (mentionState.query) {
        const desiredIndex = filteredMembers.length > 0 ? 1 : 0;
        if (mentionHighlightedIndex !== desiredIndex) {
          setMentionHighlightedIndex(desiredIndex);
        }
      } else if (mentionHighlightedIndex !== 0) {
        setMentionHighlightedIndex(0);
      }
    }
  }, [mentionState.active, mentionState.query, filteredMembers.length, mentionHighlightedIndex]);
  const hasColoredMention = useMemo(() => {
    if (!input) return false;
    const matches = input.match(GENERAL_MENTION_REGEX);
    if (!matches) return false;
    return matches.some(match => mentionColorMap[match.toLowerCase()]);
  }, [input, mentionColorMap]);
  useEffect(() => {
    if (!isProjectChat) {
      setMentionState(createMentionState());
    }
  }, [isProjectChat]);
  const updateMentionState = useCallback((value, caretOverride = null) => {
    if (!isProjectChat) {
      setMentionState(createMentionState());
      return;
    }
    const caretPosition = typeof caretOverride === 'number'
      ? caretOverride
      : (textareaRef.current ? textareaRef.current.selectionStart : value.length);
    const textBeforeCaret = value.slice(0, caretPosition);
    const mentionMatch = textBeforeCaret.match(/(^|\s)@([^\s@]*)$/);
    if (mentionMatch) {
      const query = mentionMatch[2] || '';
      const startIndex = caretPosition - query.length - 1;
      setMentionState({
        active: true,
        query,
        startIndex
      });
    } else {
      setMentionState(createMentionState());
    }
  }, [isProjectChat]);
  const handleSelectionChange = useCallback(() => {
    if (!textareaRef.current) return;
    const caret = textareaRef.current.selectionStart;
    setMentionCaretIndex(caret);
    if (mentionState.active) {
      updateMentionState(input, caret);
    }
  }, [input, mentionState.active, updateMentionState]);

  const handleMentionSelection = useCallback((option) => {
    if (!mentionState.active || mentionState.startIndex === null) {
      return;
    }
    const textarea = textareaRef.current;
    const caretPosition = textarea ? textarea.selectionStart : input.length;
    const textBeforeMention = input.slice(0, mentionState.startIndex);
    const textAfterMention = input.slice(caretPosition);
    const optionHandle = typeof option === 'string'
      ? option
      : option?.mentionHandle || option?.name || 'member';
    const rawHandle = optionHandle.replace(/\s+/g, '');
    const insertion = `@${rawHandle}`;
    const needsSpace = textAfterMention.length === 0 ? true : !/^[\s]/.test(textAfterMention[0]);
    const trailingSpace = needsSpace ? ' ' : '';
    const newValue = `${textBeforeMention}${insertion}${trailingSpace}${textAfterMention}`;
    setInput(newValue);
    setMentionState(createMentionState());
    requestAnimationFrame(() => {
      if (textarea) {
        const cursorPosition = textBeforeMention.length + insertion.length + trailingSpace.length;
        textarea.setSelectionRange(cursorPosition, cursorPosition);
        textarea.focus();
      }
    });
    setMentionCaretIndex(textBeforeMention.length + insertion.length + trailingSpace.length);
  }, [mentionState, input, setInput]);
  
  // Usage notification handlers
  const handleNotificationDismiss = async () => {
    try {
      await dismissNotification();
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };
  
  const handleToggleUsageBasedPricing = async (enabled) => {
    try {
      if (enabled) {
        await enableUsageBasedPricing();
      }
      // Refresh notification status after change
      refreshNotificationStatus();
    } catch (error) {
      console.error('Error toggling usage-based pricing:', error);
    }
  };
  
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  
  // Usage notifications hook
  const {
    notificationData,
    showNotification,
    isAdminOrOwner,
    isUsageBlocked,
    dismissNotification,
    acknowledgeBlockingNotification,
    enableUsageBasedPricing,
    refreshNotificationStatus
  } = useUsageNotifications(user);

  // Payment notifications hook
  const {
    paymentData,
    showPaymentWarning,
    hasPaymentFailure,
    dismissPaymentWarning,
    isAdminOrOwner: isPaymentAdmin
  } = usePaymentNotifications(user);

  // System status hook
  const {
    isChatBlocked,
    blockingReason,
    systemStatus
  } = useSystemStatus();

  // Clear suggestions when context changes (project vs personal)  
  useEffect(() => {
    if (!suggestionsEnabled) {
      setSuggestions([]);
      setSuggestionsVisible(false);
      setLoadingSuggestions(false);
      return;
    }
    // When switching between project and personal contexts, clear displayed suggestions
    setSuggestions([]);
    setSuggestionsVisible(false);
    setLoadingSuggestions(false);
    
    console.log('🔄 CONTEXT CHANGED - CLEARING SUGGESTIONS AND FORCING FRESH FETCH:', {
      isProjectChat,
      projectId,
      pathname: window.location.pathname
    });
    
    // Force immediate fresh fetch for new context
    if (!hasMessages && !chatId) {
      console.log('🚀 FORCING FRESH SUGGESTIONS FETCH FOR NEW CONTEXT');
      // Trigger suggestions fetch by clearing the fetching ref
      if (typeof isFetchingRef !== 'undefined' && isFetchingRef.current) {
        isFetchingRef.current = false;
      }
    }
  }, [isProjectChat, projectId, suggestionsEnabled]); // Trigger when project context changes

  // Fetch suggestions when component mounts (for first message only)
  useEffect(() => {
    if (!suggestionsEnabled) {
      if (suggestionsVisible) {
        setSuggestionsVisible(false);
      }
      if (suggestions.length > 0) {
        setSuggestions([]);
      }
      return;
    }
    try {
      // console.log('SUGGESTIONS EFFECT RUNNING', { 
      //   chatId, 
      //   hasMessages, 
      //   loadingSuggestions, 
      //   input: input?.length || 0, 
      //   suggestionsVisible, 
      //   selectedFiles: selectedFiles.length,
      //   pathname: window.location.pathname
      // });
    
    // Generate UNIQUE cache key based on context - COMPLETELY SEPARATE CACHES
    const generateCacheKey = () => {
      if (isProjectChat && projectId) {
        // Each project gets its own isolated cache
        return `midus_project_suggestions_${projectId}`;
      } else {
        // Personal/regular chats get their own isolated cache  
        return `midus_personal_suggestions`;
      }
    };
    
    const SUGGESTIONS_CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds
    
    // Clear any old contaminated caches immediately
    const clearContaminatedCaches = () => {
      const oldCacheKeys = [
        'chat_suggestions_cache',
        'suggestions_cache', 
        'project_suggestions_cache',
        'regular_suggestions_cache'
      ];
      
      oldCacheKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          console.log('🧹 Clearing old contaminated cache:', key);
          localStorage.removeItem(key);
        }
      });
    };
    
    // Clear contaminated caches on first render
    clearContaminatedCaches();
    
    // Clear all suggestion caches to ensure separation between chats
    const nukeAllSuggestionCaches = () => {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('suggestions')) {
          keys.push(key);
        }
      }
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
    };
    
    // Clear everything on component render (temporary debug measure)
    nukeAllSuggestionCaches();
    
    // Debug function to show all suggestion caches
    const debugCaches = () => {
      const caches = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('suggestions')) {
          const value = localStorage.getItem(key);
          try {
            const parsed = JSON.parse(value);
            caches.push({
              key,
              count: parsed.suggestions?.length || 0,
              context: parsed.context || 'unknown',
              age: Date.now() - (parsed.timestamp || 0)
            });
          } catch (e) {
            caches.push({ key, error: 'parse error' });
          }
        }
      }
      return caches;
    };
    
    // Show cache state on component render (for debugging)
    debugCaches();

    // If we have a chatId, messages, or URL indicates we're in a chat, immediately hide suggestions
    const isInExistingChat = chatId || hasMessages || (window.location.pathname.includes('/chat/') && window.location.pathname !== '/chat');
    
          // console.log('isInExistingChat check:', { 
      //   chatId, 
      //   hasMessages, 
      //   pathname: window.location.pathname,
      //   includesChat: window.location.pathname.includes('/chat/'),
      //   isNotRootChat: window.location.pathname !== '/chat',
      //   isInExistingChat 
      // });
    
    if (isInExistingChat) {
      // console.log('Early return - in existing chat');
      // Only update state if it's actually different to avoid unnecessary re-renders
      if (suggestionsVisible) {
        setSuggestionsVisible(false);
      }
      if (suggestions.length > 0) {
        setSuggestions([]);
      }
      return;
    }

    const getCachedSuggestions = () => {
      // TEMPORARILY DISABLE CACHING TO DEBUG CONTEXT SWITCHING
      console.log('CACHE DISABLED FOR DEBUGGING - Always fetching fresh suggestions');
      return null;
      
      /* DISABLED CACHE CODE:
      try {
        const cacheKey = generateCacheKey();
        const cached = localStorage.getItem(cacheKey);
        
        console.log('Cache lookup:', { 
          cacheKey, 
          isProjectChat, 
          projectId,
          hasCache: !!cached,
          pathname: window.location.pathname
        });
        
        if (cached) {
          const parsedCache = JSON.parse(cached);
          // Check if cache is still valid (within TTL)
          if (Date.now() - parsedCache.timestamp < SUGGESTIONS_CACHE_TTL) {
            console.log('Using cached suggestions', { 
              cacheKey,
              context: isProjectChat ? `project-${projectId}` : 'personal',
              age: Date.now() - parsedCache.timestamp,
              count: parsedCache.suggestions.length 
            });
            return parsedCache.suggestions;
          } else {
            console.log('Cache expired, removing:', cacheKey);
            localStorage.removeItem(cacheKey);
          }
        } else {
          console.log('No cache found for key:', cacheKey);
        }
      } catch (error) {
        console.error('Error reading cached suggestions:', error);
        const cacheKey = generateCacheKey();
        localStorage.removeItem(cacheKey);
      }
      return null;
      */
    };

    const cacheSuggestions = (suggestions) => {
      try {
        const cacheKey = generateCacheKey();
        const cacheData = {
          suggestions,
          timestamp: Date.now(),
          context: isProjectChat ? `project-${projectId}` : 'personal' // For debugging
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        
        console.log('💾 Cached suggestions:', { 
          cacheKey,
          context: isProjectChat ? `project-${projectId}` : 'personal',
          count: suggestions.length,
          suggestions: suggestions.map(s => s.title) // Show titles for debugging
        });
      } catch (error) {
        console.error('Error caching suggestions:', error);
      }
    };

    const fetchSuggestions = async () => {
      // TEMPORARILY HIDE SUGGESTIONS IN PROJECT CHATS
      if (isProjectChat) {
        console.log('SKIPPING SUGGESTIONS - Project chat suggestions temporarily disabled');
        return;
      }

      // Only fetch suggestions if:
      // - We DON'T have a chatId (brand new chat)
      // - There are no messages (hasMessages is false)
      // - We're not already loading suggestions
      // - Suggestions aren't already visible
      // - Input is completely empty
      // - No files are selected
      // - Not already fetching (prevents race conditions)
      // - We're specifically on the /chat page or root path (not /chat/id)
      
      const isOnNewChatPage = window.location.pathname === '/chat' || window.location.pathname === '/';
      
      console.log('SUGGESTIONS FETCH CONDITIONS:', {
        isOnNewChatPage,
        pathname: window.location.pathname,
        chatId,
        hasMessages,
        loadingSuggestions,
        suggestionsVisible,
        hasInput: !!input.trim(),
        hasFiles: selectedFiles.length > 0,
        isAlreadyFetching: isFetchingRef.current,
        isProjectChat,
        projectId
      });
      
      if (loadingSuggestions || input.trim() || selectedFiles.length > 0 || isFetchingRef.current || !isOnNewChatPage) {
        console.log('SKIPPING FETCH:', {
          loadingSuggestions,
          hasInput: !!input.trim(),
          hasFiles: selectedFiles.length > 0,
          isAlreadyFetching: isFetchingRef.current,
          isOnNewChatPage
        });
        return;
      }
      
      // ALWAYS fetch fresh suggestions when context changes (no cache, no suggestionsVisible check)
      console.log('PROCEEDING WITH FRESH SUGGESTIONS FETCH');

      // Check cache first
      const cachedSuggestions = getCachedSuggestions();
      if (cachedSuggestions !== null && cachedSuggestions.length > 0) {
        // console.log('Using cached suggestions with content:', cachedSuggestions.length);
        setSuggestions(cachedSuggestions);
        setSuggestionsVisible(true);
        return;
      } else if (cachedSuggestions !== null && cachedSuggestions.length === 0) {
        // console.log('Found cached empty suggestions, will refetch');
        // Don't return early - proceed to fetch new suggestions
      }

      try {
        isFetchingRef.current = true;
        setLoadingSuggestions(true);
        
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No token available for suggestions');
          return;
        }

        // For brand new chats, we need to generate suggestions based on user context
        // Include project context for project chats
        const params = {};
        if (isProjectChat && projectId) {
          params.projectId = projectId;
          console.log('Fetching PROJECT suggestions for:', projectId);
        } else {
          console.log('👤 Fetching PERSONAL suggestions (no project context)');
        }
        
        const response = await axios.get(
          getApiUrl('chat/suggestions'),
          {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params
          }
        );

        if (response.data.success) {
          console.log('📥 Received suggestions:', {
            source: response.data.source || 'unknown',
            context: isProjectChat ? `project-${projectId}` : 'personal',
            count: response.data.suggestions.length,
            titles: response.data.suggestions.map(s => s.title)
          });
          
          setSuggestions(response.data.suggestions);
          
          // Only show suggestions if there are actually suggestions to show
          if (response.data.suggestions.length > 0) {
            setSuggestionsVisible(true);
          }
          
          // TEMPORARILY DISABLE FRONTEND CACHING FOR DEBUG
          // cacheSuggestions(response.data.suggestions);
        }

      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        isFetchingRef.current = false;
        setLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
    } catch (error) {
      console.error('Error in suggestions effect:', error);
    }
  }, [isProjectChat, projectId, hasMessages, chatId, input, selectedFiles.length, suggestionsEnabled]); // Add context dependencies to re-fetch when context changes
  


  // Calculate dynamic border radius based on container height (squircle uses these values)
  const getDynamicBorderRadius = (height) => {
    const baseHeight = 80;
    const baseRadius = 42; // Larger radius for squircle (iOS-style continuous curvature)
    const maxRadius = 50;
    const extraHeight = Math.max(0, height - baseHeight);
    const extraRadius = Math.min(12, extraHeight * 0.06);
    const r = Math.round(Math.min(maxRadius, baseRadius + extraRadius));
    return getPlainRadiusForContinuousCornerDesignPx(r);
  };

  // Handle clicks outside the dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on model dropdown or model button
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target) &&
          modelButtonRef.current && !modelButtonRef.current.contains(event.target)) {
        setIsModelDropdownOpen(false);
      }
      // Check if click is on tools dropdown or tools button
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target) &&
          toolsButtonRef.current && !toolsButtonRef.current.contains(event.target)) {
        setIsToolsDropdownOpen(false);
        setHoveredTool(null);
      }
      // Check if click is outside connectors (button + dropdown; dropdown is in portal so check both)
      const insideConnectors = connectorsContainerRef.current?.contains(event.target) || connectorsDropdownRef.current?.contains(event.target);
      if (!insideConnectors) {
        setIsConnectorsDropdownOpen(false);
        setHoveredTool(null);
      }
    };

    if (isModelDropdownOpen || isToolsDropdownOpen || isConnectorsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModelDropdownOpen, isToolsDropdownOpen, isConnectorsDropdownOpen]);

  // Handle window resize to recalculate dropdown positions
  useEffect(() => {
    const handleResize = () => {
      // Recalculate positions for any open dropdowns
      if (isModelDropdownOpen) {
        const position = calculateDropdownPosition(modelButtonRef, showTooltipsAbove);
        setModelDropdownPosition(position);
      }
      if (isToolsDropdownOpen) {
        const position = calculateDropdownPosition(toolsButtonRef, showTooltipsAbove, true);
        setToolsDropdownPosition(position);
      }
      if (isConnectorsDropdownOpen) {
        const position = calculateDropdownPosition(connectorsContainerRef, showTooltipsAbove, true);
        setConnectorsDropdownPosition(position);
      }
    };

    if (isModelDropdownOpen || isToolsDropdownOpen || isConnectorsDropdownOpen) {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isModelDropdownOpen, isToolsDropdownOpen, isConnectorsDropdownOpen]);

  // Set up global drag and drop listeners
  useEffect(() => {
    // Add event listeners to the window for global drag and drop
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      // Clean up event listeners
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [selectedFiles, suggestionsVisible]); // Dependencies for the handlers

  // Helper function to ensure consistent API URL format
  const getApiUrl = (endpoint) => {
    // Get the base API URL 
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081';
    
    // Clean the URL and normalize format
    let baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    
    // Check if the baseUrl already includes '/api' and handle endpoint appropriately
    if (baseUrl.endsWith('/api')) {
      console.log('API URL already ends with /api:', baseUrl);
      // Remove '/api' from the beginning of the endpoint if present
      const cleanEndpoint = endpoint.startsWith('api/') ? endpoint.substring(4) : endpoint;
      return `${baseUrl}/${cleanEndpoint}`;
    }
    
    // If endpoint doesn't start with 'api/', add it
    const cleanEndpoint = endpoint.startsWith('api/') ? endpoint : `api/${endpoint}`;
    return `${baseUrl}/${cleanEndpoint}`;
  };

  // Sync internal model state when parent (e.g. header selector) changes selection
  useEffect(() => {
    if (selectedModelFromParent?.provider != null && selectedModelFromParent?.id != null) {
      setSelectedProvider(selectedModelFromParent.provider);
      setSelectedModel(selectedModelFromParent.id);
    }
  }, [selectedModelFromParent?.provider, selectedModelFromParent?.id]);

  // Load persisted state on component mount
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        
        // Restore text input
        if (parsedState.input && parsedState.input.trim()) {
          setInput(parsedState.input);
        }
        
        // Note: File restoration removed to prevent localStorage quota issues
        // Files will be handled via S3 storage in the new implementation
        
        // Restore model from localStorage only for an existing chat; new chat uses user default via sync effect
        if (chatId) {
          if (parsedState.selectedProvider && parsedState.selectedModel) {
            const legacyAuto =
              parsedState.selectedProvider === 'auto' && parsedState.selectedModel === 'auto';
            if (!legacyAuto) {
              setSelectedProvider(parsedState.selectedProvider);
              setSelectedModel(parsedState.selectedModel);
              onModelSelect?.(parsedState.selectedProvider, parsedState.selectedModel);
            }
          }
        }
        
        // Restore tool selection from localStorage (DB takes precedence when user loads)
        if (parsedState.selectedTools && Array.isArray(parsedState.selectedTools)) {
          setUserEnabledTools(parsedState.selectedTools);
        } else if (parsedState.selectedTool) {
          setUserEnabledTools([parsedState.selectedTool]);
        }
        
        // Restore last improved prompt if available
        if (parsedState.lastImprovedPrompt) {
          setLastImprovedPrompt(parsedState.lastImprovedPrompt);
        }
      } catch (error) {
        console.error('Error parsing saved chat input state:', error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only restore; chatId is initial shell only
  }, [setInput]);

  // Load userEnabledTools: prefer DB over localStorage so tools sync across devices/sessions (local vs production)
  const hasLoadedToolsFromDbRef = useRef(false);
  useEffect(() => {
    const fromDb = user?.preferences?.enabledTools;
    if (Array.isArray(fromDb)) {
      hasLoadedToolsFromDbRef.current = true;
      setUserEnabledTools(fromDb);
      return;
    }
    // Only use localStorage when user/DB not ready; don't set ref so we can update from DB when user loads
    if (hasLoadedToolsFromDbRef.current) return;
    const fromLocal = (() => {
      try {
        const s = localStorage.getItem(STORAGE_KEY);
        if (!s) return null;
        const p = JSON.parse(s);
        return Array.isArray(p?.selectedTools) ? p.selectedTools : null;
      } catch {
        return null;
      }
    })();
    if (fromLocal !== null) {
      setUserEnabledTools(fromLocal);
    }
  }, [user?.preferences?.enabledTools]);

  // Save userEnabledTools to localStorage and DB whenever they change
  useEffect(() => {
    if (!isSuperBucketMode) {
      const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...currentState,
        selectedTools: userEnabledTools,
        lastImprovedPrompt: currentState.lastImprovedPrompt || null
      }));
    }
  }, [userEnabledTools, isSuperBucketMode]);

  // Persist enabledTools to database
  const saveEnabledToolsToApi = useCallback(async (tools) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const url = getApiUrl('api/users/me');
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          preferences: {
            ...user?.preferences,
            enabledTools: tools
          }
        })
      });
    } catch (err) {
      console.warn('Failed to save enabled tools to server:', err);
    }
  }, [user?.preferences]);

  // Verify token is valid
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const token = localStorage.getItem('token');
        // Check if token exists
        
        if (!token) {
          console.error('No token found in localStorage');
          return false;
        }
        
        // Try to call a protected endpoint to verify token
        const url = getApiUrl('api/auth/me');
        // Testing token validity
        
        try {
          const testResponse = await axios.get(url, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
                      // Token verification succeeded
          return true;
        } catch (error) {
          console.error('Token verification failed:', error.message);
          if (error.response) {
            console.error('Status:', error.response.status);
            console.error('Response:', error.response.data);
          }
          return false;
        }
      } catch (error) {
        console.error('Error in token verification:', error);
        return false;
      }
    };
    
    // Run token verification
    verifyToken().then(isValid => {
              // Token validity checked
    });
    
    // Continue with fetching models
    const fetchModels = async () => {
      // Starting model fetch
      try {
        setIsLoading(true);
        setError('');
        
        const token = localStorage.getItem('token');
                  // Token availability checked
        
        if (!token) {
          console.error('No token available for authentication');
          throw new Error('Authentication required');
        }
        
        // Log the request we're about to make
        const modelsUrl = getApiUrl('admin/models');
        console.log(`Attempting to fetch from: ${modelsUrl} with auth token`);
        
        try {
          // First try the authenticated endpoint that respects organization preferences
          const response = await axios.get(modelsUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (Array.isArray(response.data) && response.data.length > 0) {
            setAvailableModels(response.data);
          } else {
            console.warn('Auth endpoint returned no models:', response.data);
            throw new Error('No models available');
          }
        } catch (authError) {
          console.error('Auth endpoint error:', authError.message);
          console.log('Falling back to default endpoint...');
          
          // Fall back to the default endpoint
          try {
            const fallbackUrl = getApiUrl('models/default');
            console.log(`Trying fallback: ${fallbackUrl}`);
            const fallbackResponse = await axios.get(fallbackUrl);
            
            console.log('Fallback response status:', fallbackResponse.status);
            console.log('Fallback response data:', fallbackResponse.data);
            
            if (Array.isArray(fallbackResponse.data) && fallbackResponse.data.length > 0) {
              console.log(`Got ${fallbackResponse.data.length} models from fallback endpoint`);
              setAvailableModels(fallbackResponse.data);
              setError('Using default models. Contact administrator to configure models.');
            } else {
              console.error('Fallback endpoint returned empty or invalid data');
              throw new Error('No models available from fallback');
            }
          } catch (fallbackError) {
            console.error('Fallback endpoint error:', fallbackError.message);
            setError('Could not load any models. Please contact administrator.');
            
            // Log what's happening with state
            console.error('Current state after all errors:', {
              availableModels: [],
              selectedProvider,
              selectedModel,
              isLoading: false,
              error: 'Could not load any models. Please contact administrator.'
            });
          }
        }
      } catch (error) {
        console.error('Top-level error in fetchModels:', error.message);
        if (!error.message.includes('Please contact administrator')) {
          setError('Could not load any models. Please contact administrator.');
        }
      } finally {
        // Model fetch completed
        setIsLoading(false);
      }
    };

    fetchModels();
  }, []);

  // Once models (and optionally user default preference) are available, resolve concrete Midus default
  // instead of leaving session on auto/2.6 label; re-run when default key changes (e.g. header "Change default").
  useEffect(() => {
    if (!availableModels?.length) return;
    const next = chatId
      ? resolveInitialChatModelSelection(
          availableModels,
          user?.preferences?.defaultChatModelKey
        )
      : resolveModelSelectionForNewChat(
          availableModels,
          user?.preferences?.defaultChatModelKey
        );
    setSelectedProvider(next.provider);
    setSelectedModel(next.model);
    onModelSelect?.(next.provider, next.model);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onModelSelect identity varies; avoid loops
  }, [availableModels, user?.id, user?.preferences?.defaultChatModelKey, chatId]);

  // Save lightweight state to localStorage (no file data to prevent quota issues)
  useEffect(() => {
    const state = { 
      input,
      selectedProvider,
      selectedModel,
      selectedTools,  // Include selectedTools to prevent it from being cleared
      lastImprovedPrompt  // Include lastImprovedPrompt for persistence
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving chat input state to localStorage:', error);
      // If localStorage is full, clear it and try again with minimal state
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          selectedProvider: selectedProvider || '',
          selectedModel: selectedModel || '',
          selectedTools: selectedTools,  // Preserve selectedTool even in minimal state
          lastImprovedPrompt: lastImprovedPrompt  // Preserve lastImprovedPrompt even in minimal state
        }));
      } catch (retryError) {
        console.error('Failed to save even minimal state:', retryError);
      }
    }
  }, [input, selectedProvider, selectedModel, selectedTools, lastImprovedPrompt]);

  // Track chat input height changes - including notification area
  useEffect(() => {
    if (!outerContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        // Set container height from the chat input for border radius calculation
        if (chatInputRef.current) {
          setContainerHeight(chatInputRef.current.offsetHeight);
        }
        // Pass the total height (including notification) to parent
        if (onHeightChange) {
          onHeightChange(height);
        }
      }
    });

    // Observe the outer container that includes the notification
    resizeObserver.observe(outerContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [onHeightChange]);

  // Additional effect to track notification visibility changes
  useEffect(() => {
    if (!outerContainerRef.current || !onHeightChange) return;

    const totalHeight = outerContainerRef.current.offsetHeight;
    onHeightChange(totalHeight);
  }, [showNotification, onHeightChange]);

  // Check scrollbar visibility based on scroll position
  const checkScrollbarVisibility = () => {
    if (textareaRef.current) {
      const scrollTop = textareaRef.current.scrollTop;
      setShowScrollbar(scrollTop >= 50);
    }
  };

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      
      // Check scrollbar visibility after height adjustment
      setTimeout(() => {
        checkScrollbarVisibility();
      }, 0);
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  // Handle textarea scroll for scrollbar visibility
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleScroll = () => {
      checkScrollbarVisibility();
    };

    textarea.addEventListener('scroll', handleScroll);
    
    // Also check initial scroll position
    checkScrollbarVisibility();
    
    return () => textarea.removeEventListener('scroll', handleScroll);
  }, [input]); // Re-attach when input changes

  useEffect(() => {
    // Initial focus
    if (textareaRef.current) {
      textareaRef.current.focus();
      // Force the cursor to show by briefly removing and restoring focus
      textareaRef.current.blur();
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [setInput]);

  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
      // Same technique when re-focusing after sending
      textareaRef.current.blur();
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [disabled]);

  // WebSocket connection for typing events
  useEffect(() => {
    // Only set up typing events for project chats
    if (!isProjectChat) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const buildTypingWsUrl = () => {
      const normalizeApiHost = (url) => url.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const appendToken = (baseUrl) => `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}token=${token}`;
      const envWsUrl = process.env.REACT_APP_WS_URL;
      
      if (envWsUrl) {
        return appendToken(envWsUrl.replace(/\/$/, ''));
      }
      
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8081/api' : null);
      
      if (apiUrl) {
        return appendToken(`${protocol}//${normalizeApiHost(apiUrl)}/api/ws`);
      }
      
      return appendToken(`${protocol}//${window.location.host}/api/ws`);
    };

    // Create WebSocket connection for typing events
    const ws = new WebSocket(buildTypingWsUrl());
    
    ws.onopen = () => {
      console.log('Typing WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Typing WebSocket message:', data);
        
        // Handle typing events here - this will be passed from parent component
        // The parent component will handle these events and update the props
      } catch (error) {
        console.error('Error parsing typing WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('Typing WebSocket disconnected');
    };

    // Send typing events
    const sendTypingEvent = (isTyping, chatId) => {
      if (ws.readyState === WebSocket.OPEN && chatId) {
        ws.send(JSON.stringify({
          type: 'user_typing',
          chatId: chatId,
          isTyping: isTyping,
          timestamp: Date.now()
        }));
        console.log(`Sent typing event: ${isTyping ? 'started' : 'stopped'} typing`);
      }
    };

    // Store WebSocket reference for cleanup
    const wsRef = { current: ws, sendTypingEvent };
    
    // Cleanup
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [isProjectChat]);

  // Helper function to send typing events
  const sendTypingEvent = useCallback((isTyping) => {
    if (!isProjectChat) return;
    
    // Get chatId from URL or props
    const currentPath = window.location.pathname;
    const chatIdMatch = currentPath.match(/\/chat\/([^\/]+)/);
    const chatId = chatIdMatch ? chatIdMatch[1] : null;
    
    if (!chatId) return;

    // Send via existing WebSocket service if available
    const token = localStorage.getItem('token');
    if (!token) return;

    // For now, we'll use a simple fetch to a typing endpoint instead of managing separate WebSocket
    // This is more reliable than managing multiple WebSocket connections
    const sendTypingNotification = async () => {
      try {
        const response = await fetch(getApiUrl('api/typing'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            chatId: chatId,
            isTyping: isTyping,
            timestamp: Date.now()
          })
        });
        
        if (response.ok) {
          console.log(`Typing notification sent: ${isTyping ? 'started' : 'stopped'}`);
        }
      } catch (error) {
        console.error('Error sending typing notification:', error);
      }
    };

    sendTypingNotification();
  }, [isProjectChat]);

  // Handle typing detection
  const handleTypingStart = useCallback(() => {
    if (!isProjectChat) return;
    
    // Set user as typing if not already
    if (!isUserTyping) {
      setIsUserTyping(true);
    }
    
    // Always send typing event to reset timeout for other users
    sendTypingEvent(true);
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing after 1 minute of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsUserTyping(false);
      sendTypingEvent(false);
    }, TYPING_TIMEOUT);
  }, [isProjectChat, isUserTyping, sendTypingEvent]);

  const handleTypingStop = useCallback(() => {
    if (!isProjectChat) return;
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (isUserTyping) {
      setIsUserTyping(false);
      sendTypingEvent(false);
    }
  }, [isProjectChat, isUserTyping, sendTypingEvent]);

  // Modified onChange handler to include typing detection
  const handleInputChange = (e) => {
    spellCheck.handleInputChange(e, (correctedEvent) => {
      const newValue = correctedEvent.target.value;
      setInput(newValue);
      adjustTextareaHeight();
      const caretPos = correctedEvent.target.selectionStart;
      setMentionCaretIndex(caretPos);
      updateMentionState(newValue, caretPos);
      
      // Show/hide suggestions based on input content or files
      if (suggestionsEnabled) {
        if ((newValue.trim() || selectedFiles.length > 0) && suggestionsVisible) {
          setSuggestionsVisible(false);
        } else if (!newValue.trim() && selectedFiles.length === 0 && !suggestionsVisible && suggestions.length > 0 && !chatId && !hasMessages) {
          setSuggestionsVisible(true);
        }
      } else if (suggestionsVisible) {
        setSuggestionsVisible(false);
      }
      
      // Clear lastImprovedPrompt if user clears the input completely
      if (!newValue.trim()) {
        setLastImprovedPrompt(null);
        
        // Update localStorage immediately when clearing
        const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          ...currentState,
          lastImprovedPrompt: null
        }));
      }
      
      // Handle typing status based on text presence for project chats
      if (isProjectChat) {
        if (newValue.trim()) {
          // Text exists - user should be considered typing
          // This will reset the 1-minute timeout
          handleTypingStart();
        } else {
          // No text - user should not be considered typing
          handleTypingStop();
        }
      }

    }, textareaRef);
  };

  // Modified keyboard handler to detect typing stops
  const handleKeyPress = (e) => {
    // Check for mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (mentionState.active && mentionOptionsCount > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionHighlightedIndex(prev => (prev + 1) % mentionOptionsCount);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionHighlightedIndex(prev => (prev - 1 + mentionOptionsCount) % mentionOptionsCount);
        return;
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault();
        const option = mentionHighlightedIndex === 0
          ? { mentionHandle: 'midus' }
          : filteredMembers[mentionHighlightedIndex - 1];
        handleMentionSelection(option);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState(createMentionState());
        return;
      }
    }
    
    // Check for Ctrl/Cmd + Z to undo last spell correction
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && spellCheck.corrections.length > 0) {
      e.preventDefault();
      const correctedText = spellCheck.undoLastCorrection(input, textareaRef.current);
      if (correctedText !== input) {
        setInput(correctedText);
      }
      return;
    }
    
    // Check if Enter key was pressed without shift and not in a mobile device
    if (e.key === 'Enter' && !e.shiftKey && !isMobile && !disabled) {
      e.preventDefault();
      // Stop typing when sending message
      handleTypingStop();
      handleSubmit();
    }
  };

  // Stop typing when input is cleared or component unmounts
  useEffect(() => {
    if (!input.trim()) {
      handleTypingStop();
    }
  }, [input, handleTypingStop]);

  useEffect(() => {
    return () => {
      // Clean up typing state on unmount
      handleTypingStop();
    };
  }, [handleTypingStop]);

  // Handle SuperBucket organizational intelligence queries  
  const handleSuperBucketQuery = (query) => {
    console.log('SuperBucket query initiated:', query);
    
    // 🚀 IMMEDIATE UI FEEDBACK: Show user message and loading indicator right away
    // This mimics exactly how regular messages work - user message appears immediately
    // The streaming service will handle the SuperBucket processing in the background
    handleSend(query, null, null, { isSuperBucketQuery: true });
  };

  const handleSubmit = async (e, options = {}) => {
    // If event is provided, prevent default form submission
    if (e) e.preventDefault();
    
    // If bot is responding, cancel the response
    if (disabled && cancelResponse) {
      console.log('Attempting to cancel response via button click');
      cancelResponse();
      return;
    }
    
    console.log('handleSubmit called', {
      hasFiles: selectedFiles.length > 0,
      hasInput: !!input.trim(),
      selectedFilesCount: selectedFiles.length,
      provider: selectedProvider,
      model: selectedModel
    });
    
    const forceAskMidus = Boolean(options.forceAskMidus);
    const shouldUseAiByDefault = !isProjectChat || !handleDiscussionSend;
    const requiresAi = forceAskMidus || shouldUseAiByDefault || selectedTools.length > 0 || isSuperBucketMode || hasMidusMention;
    
    if (selectedFiles.length > 0) {
      console.log('Submitting message with files:', selectedFiles.length);
      submitFiles();
    } else if (input.trim()) {
      console.log('Attempting to send message via button');
      
      // Handle SuperBucket queries differently
      // console.log('Checking SuperBucket mode:', isSuperBucketMode, 'selectedTool:', selectedTool);
      if (!requiresAi && isProjectChat) {
        console.log('Posting discussion-only message');
        handleDiscussionSend(input.trim());
        setInput('');
        setMentionState(createMentionState());
        setLastImprovedPrompt(null);
        clearSuggestionsCache();
        const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          input: '',
          selectedProvider: currentState.selectedProvider || selectedProvider,
          selectedModel: currentState.selectedModel || selectedModel,
          selectedTools: selectedTools,
          lastImprovedPrompt: null
        }));
      } else if (selectedTools.includes('superbucket')) {
        console.log('Processing as SuperBucket query');
        
        // 🚀 IMMEDIATE UI FEEDBACK: Clear input immediately like regular messages
        const queryText = input.trim();
        setInput('');
        setMentionState(createMentionState());
        setLastImprovedPrompt(null); // Clear last improved prompt when sending
        
        // Clear suggestions cache since user patterns may have changed
        clearSuggestionsCache();
        
        // Update localStorage to clear input but keep model and tool selection
        const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          input: '',
          selectedProvider: currentState.selectedProvider || selectedProvider,
          selectedModel: currentState.selectedModel || selectedModel,
          selectedTools: selectedTools,
          lastImprovedPrompt: null
        }));
        
        handleSuperBucketQuery(queryText);
      } else {
        console.log('💬 Processing as regular chat');
        console.log('Selected tools:', selectedTools);
        const messageToSend = input.trim();
        if (!selectedProvider || !selectedModel) {
          toast.error('Select a model to continue.');
          return;
        }
        const toolSelection = {};
        if (selectedTools.includes('search')) toolSelection.forceWebSearch = true;
        if (selectedTools.includes('deep-research')) toolSelection.useDeepResearch = true;
        if (selectedTools.includes('create-image')) toolSelection.useImageGeneration = true;
        if (selectedTools.includes('hubspot')) toolSelection.useHubSpotMcp = true;
        if (selectedTools.includes('google-drive')) toolSelection.useGoogleDrive = true;
        if (selectedTools.includes('gmail')) toolSelection.useGmail = true;
        if (selectedTools.includes('github')) toolSelection.useGitHub = true;
        if (selectedTools.includes('notion')) toolSelection.useNotion = true;
        if (selectedTools.includes('google-calendar')) toolSelection.useGoogleCalendar = true;
        if (selectedTools.includes('outlook-calendar')) toolSelection.useOutlookCalendar = true;
        if (selectedTools.includes('outlook-mail')) toolSelection.useOutlook = true;
        // useSharePoint set below after connection check (may be removed if both disconnected)
        console.log('Tool selection object:', toolSelection);

        // 🚀 OPTIMISTIC UI: Show user message + loading indicator immediately (before any network calls)
        // Critical for low-connectivity - avoids lag where nothing appears until server responds
        let rollback = null;
        if (onOptimisticSend) {
          rollback = onOptimisticSend(messageToSend);
        }

        // Validation for Gmail/Outlook - runs after optimistic update; rollback if connection check fails
        if (selectedTools.includes('gmail')) {
          try {
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
            const res = await axios.get(`${apiUrl}/auth/google-drive/status?connector=gmail`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.data?.connected) {
              if (rollback) rollback();
              toast.error('Connect Gmail in Connectors to use this tool');
              openConnectToolsModal();
              return;
            }
          } catch (err) {
            if (rollback) rollback();
            toast.error('Could not verify Gmail connection. Please try again.');
            return;
          }
        }
        if (selectedTools.includes('outlook-mail')) {
          try {
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
            const res = await axios.get(`${apiUrl}/auth/outlook/status`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.data?.connected) {
              if (rollback) rollback();
              toast.error('Connect Outlook in Connectors to use this tool');
              openConnectToolsModal();
              return;
            }
          } catch (err) {
            if (rollback) rollback();
            toast.error('Could not verify Outlook connection. Please try again.');
            return;
          }
        }
        if (selectedTools.includes('outlook-calendar')) {
          try {
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
            const res = await axios.get(`${apiUrl}/auth/outlook-calendar/status`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.data?.connected) {
              if (rollback) rollback();
              toast.error('Connect Outlook Calendar in Connectors to use this tool');
              openConnectToolsModal();
              return;
            }
          } catch (err) {
            if (rollback) rollback();
            toast.error('Could not verify Outlook Calendar connection. Please try again.');
            return;
          }
        }
        if (selectedTools.includes('github')) {
          try {
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
            const res = await axios.get(`${apiUrl}/auth/github/status`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.data?.connected) {
              if (rollback) rollback();
              toast.error('Connect GitHub in Connectors to use this tool');
              openConnectToolsModal();
              return;
            }
          } catch (err) {
            if (rollback) rollback();
            toast.error('Could not verify GitHub connection. Please try again.');
            return;
          }
        }
        if (selectedTools.includes('notion')) {
          try {
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
            const res = await axios.get(`${apiUrl}/auth/notion/status`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.data?.connected) {
              if (rollback) rollback();
              toast.error('Connect Notion in Connectors to use this tool');
              openConnectToolsModal();
              return;
            }
          } catch (err) {
            if (rollback) rollback();
            toast.error('Could not verify Notion connection. Please try again.');
            return;
          }
        }
        if (selectedTools.includes('sharepoint') || selectedTools.includes('onedrive')) {
          try {
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
            const [spRes, odRes] = await Promise.all([
              axios.get(`${apiUrl}/auth/sharepoint/status`, { headers: { Authorization: `Bearer ${token}` } }),
              axios.get(`${apiUrl}/auth/onedrive/status`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            const hasSp = spRes.data?.connected;
            const hasOd = odRes.data?.connected;
            if (!hasSp && !hasOd) {
              // Both disconnected - remove from selection and proceed (don't block for stale state)
              setUserEnabledTools((prev) => {
                const next = prev.filter((id) => id !== 'sharepoint' && id !== 'onedrive');
                saveEnabledToolsToApi(next);
                if (setUser && user) {
                  setUser({ ...user, preferences: { ...user.preferences, enabledTools: next } });
                }
                return next;
              });
              // Don't set toolSelection.useSharePoint - fall through and send without it
            } else {
              toolSelection.useSharePoint = true;
            }
          } catch (err) {
            if (rollback) rollback();
            toast.error('Could not verify connection. Please try again.');
            return;
          }
        }
        
        // Pass skipOptimisticUpdate when we already did the optimistic update above
        const sendToolSelection = { ...toolSelection, skipOptimisticUpdate: !!onOptimisticSend };
        handleSend(messageToSend, selectedProvider, selectedModel, sendToolSelection);
        
        setInput('');
        setMentionState(createMentionState());
        setLastImprovedPrompt(null); // Clear last improved prompt when sending
        
        // Clear suggestions cache since user patterns may have changed
        clearSuggestionsCache();
        
        // Update localStorage to clear input but keep model and tool selection
        const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          input: '',
          selectedProvider: currentState.selectedProvider || selectedProvider,
          selectedModel: currentState.selectedModel || selectedModel,
          selectedTools: selectedTools,
          lastImprovedPrompt: null
        }));
      }
    }
  };

  const handleAskMidus = () => {
    if (controlsDisabled) return;
    handleSubmit(null, { forceAskMidus: true });
  };

  const clearLevels = useCallback(() => {
    setLevels(Array(MAX_LEVEL_HISTORY).fill(INITIAL_LEVEL));
  }, []);

  const pushLevel = useCallback((value) => {
    setLevels((prev) => {
      const base = prev.length >= MAX_LEVEL_HISTORY ? prev.slice(1) : prev;
      return [...base, value];
    });
  }, []);

  const stopMetering = useCallback(() => {
    if (meteringIntervalRef.current) {
      clearInterval(meteringIntervalRef.current);
      meteringIntervalRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const handleVoiceRecordComplete = useCallback(async ({ durationMs, audioBlob }) => {
    if (!audioBlob) {
      toast.error(TRANSCRIPTION_WARNING_MESSAGE);
      return;
    }
    if (durationMs && durationMs < MIN_TRANSCRIPTION_DURATION_MS) {
      toast.error(TRANSCRIPTION_WARNING_MESSAGE);
      return;
    }

    voiceTranscribeControllerRef.current?.abort();
    const abortController = new AbortController();
    voiceTranscribeControllerRef.current = abortController;
    setVoiceTranscribing(true);

    try {
      const result = await transcribeAudioRecording({
        audioBlob,
        signal: abortController.signal,
      });

      const normalized = result.text?.trim();
      if (!normalized) {
        toast.error(TRANSCRIPTION_WARNING_MESSAGE);
        return;
      }

      const alphanumericContent = /[a-z0-9]/i.test(normalized);
      if (!alphanumericContent) {
        toast.error(TRANSCRIPTION_WARNING_MESSAGE);
        return;
      }

      const sanitized = normalized
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const isHallucination =
        sanitized &&
        TRANSCRIPTION_HALLUCINATION_PATTERNS.some((pattern) => pattern.test(sanitized));

      if (isHallucination) {
        toast.error(TRANSCRIPTION_WARNING_MESSAGE);
        return;
      }

      setInput((prev) => {
        if (!prev) return normalized;
        const needsSpace = /\s$/.test(prev);
        return `${prev}${needsSpace ? '' : ' '}${normalized}`;
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      toast.error(TRANSCRIPTION_WARNING_MESSAGE);
    } finally {
      if (voiceTranscribeControllerRef.current === abortController) {
        voiceTranscribeControllerRef.current = null;
      }
      setVoiceTranscribing(false);
    }
  }, [setInput]);

  const stopRecording = useCallback(
    async (reason = 'complete') => {
      if (!mediaRecorderRef.current && !isRecording) return;

      setIsRecording(false);
      stopMetering();
      clearLevels();
      setRecordingDuration(0);
      const duration =
        recordingStartRef.current !== null ? Date.now() - recordingStartRef.current : 0;
      recordingStartRef.current = null;

      const recorder = mediaRecorderRef.current;
      mediaRecorderRef.current = null;

      let audioBlob = null;
      let stopFailed = false;

      if (recorder && recorder.state !== 'inactive') {
        try {
          await new Promise((resolve, reject) => {
            const prevOnStop = recorder.onstop;
            recorder.onstop = () => {
              prevOnStop?.();
              resolve();
            };
            recorder.onerror = (e) => {
              reject(e);
            };
            recorder.stop();
          });
          if (recordingChunksRef.current.length > 0) {
            audioBlob = new Blob(recordingChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
          }
          recordingChunksRef.current = [];
        } catch (err) {
          stopFailed = true;
          console.warn('Failed to stop audio recording', err);
        }
      }

      const isTooShort = duration < 400;
      const handledAsComplete = reason === 'complete' && !isTooShort;

      if (handledAsComplete && !stopFailed && audioBlob) {
        handleVoiceRecordComplete({ durationMs: duration, audioBlob });
      }
    },
    [isRecording, handleVoiceRecordComplete, stopMetering, clearLevels]
  );

  const startRecording = useCallback(async () => {
    if (isRecording) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      recordingChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      };

      mediaStreamRef.current = stream;
      recorder.start();
      mediaRecorderRef.current = recorder;
      recordingStartRef.current = Date.now();
      setIsRecording(true);
      setRecordingDuration(0);
      clearLevels();

      // Audio metering via Web Audio API
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        meteringIntervalRef.current = setInterval(() => {
          if (!audioContextRef.current) return;
          analyser.getByteFrequencyData(dataArray);
          const sum = dataArray.reduce((a, b) => a + b, 0);
          const avg = dataArray.length > 0 ? sum / dataArray.length : 0;
          // Map 0-255 to dB-like range (-40 to ~0)
          const level = avg > 0 ? 20 * Math.log10(avg) - 60 : INITIAL_LEVEL;
          pushLevel(Math.max(INITIAL_LEVEL, level));
        }, METERING_INTERVAL_MS);
      } catch (meterErr) {
        console.warn('Audio metering unavailable', meterErr);
        meteringIntervalRef.current = setInterval(() => pushLevel(INITIAL_LEVEL + 5), METERING_INTERVAL_MS);
      }

      // Duration tick
      durationIntervalRef.current = setInterval(() => {
        if (recordingStartRef.current) {
          setRecordingDuration(Date.now() - recordingStartRef.current);
        }
      }, 200);
    } catch (err) {
      console.warn('Failed to start recording', err);
      toast.error('Please enable microphone access to record voice notes.');
    }
  }, [isRecording, clearLevels, pushLevel]);

  const toggleVoiceRecording = useCallback(() => {
    try {
      if (isRecording) {
        stopRecording('complete');
      } else {
        startRecording();
      }
    } catch (e) {
      console.error('Error toggling voice recording:', e);
      setIsRecording(false);
    }
  }, [isRecording, startRecording, stopRecording]);

  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const processIncomingFiles = useCallback(async (incomingFiles) => {
    if (incomingFiles.length === 0) return;

    const { files, skippedFiles, failedConversions } = await normalizeImageUploads(incomingFiles);
    if (files.length === 0) {
      if (skippedFiles.length > 0) {
        alert(`Unable to process file(s): ${skippedFiles.join(', ')}. Please use PNG, JPG, WEBP, or GIF.`);
      }
      return;
    }

    if (failedConversions.length > 0 && process.env.REACT_APP_DEBUG_UPLOADS === 'true') {
      console.warn('Browser HEIC conversion unavailable for file(s):', failedConversions.join(', '));
    }

    // Check total file count limit (current + new files)
    if (selectedFiles.length + files.length > 10) {
      alert(`Maximum 10 files allowed. You currently have ${selectedFiles.length} files. You can add ${10 - selectedFiles.length} more.`);
      return;
    }

    // Check each file size (limit to 100MB)
    const validFiles = files.filter(file => {
      if (file.size > 100 * 1024 * 1024) {
        alert(`File ${file.name} exceeds the 100MB size limit and will be skipped`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Add to existing files
    setSelectedFiles(prev => [...prev, ...validFiles]);
    
    // Hide suggestions immediately when files are added
    if (suggestionsVisible) {
      setSuggestionsVisible(false);
    }
    
    // Convert files to data URLs for preview only (not for storage)
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFileDataUrls(prev => [...prev, event.target.result]);
      };
      reader.onerror = () => {
        console.error('Error reading file');
      };
      reader.readAsDataURL(file);
    });
    
    // Focus the textarea for context input
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [selectedFiles.length, suggestionsVisible]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    await processIncomingFiles(files);

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    await processIncomingFiles(files);
  };

  // Global drag and drop handlers for window
  const handleWindowDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleWindowDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show drag state if files are being dragged
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleWindowDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide drag state if we're leaving the window entirely
    if (e.clientX === 0 && e.clientY === 0) {
      setIsDragOver(false);
    }
  };

  const handleWindowDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    await processIncomingFiles(files);
  };

  const submitFiles = async () => {
    if (selectedFiles.length === 0) return;

    try {
      // Check Gmail connection if Gmail tool is selected
      if (selectedTools.includes('gmail')) {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
        const res = await axios.get(`${apiUrl}/auth/google-drive/status?connector=gmail`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.data?.connected) {
          toast.error('Connect Gmail in Connectors to use this tool');
          openConnectToolsModal();
          return;
        }
      }
      // Check Outlook connection if Outlook tool is selected
      if (selectedTools.includes('outlook-mail')) {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
        const res = await axios.get(`${apiUrl}/auth/outlook/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.data?.connected) {
          toast.error('Connect Outlook in Connectors to use this tool');
          openConnectToolsModal();
          return;
        }
      }
      // Check Outlook Calendar connection if Outlook Calendar tool is selected
      if (selectedTools.includes('outlook-calendar')) {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
        const res = await axios.get(`${apiUrl}/auth/outlook-calendar/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.data?.connected) {
          toast.error('Connect Outlook Calendar in Connectors to use this tool');
          openConnectToolsModal();
          return;
        }
      }
      // Check SharePoint/OneDrive connection if tool is selected
      let effectiveSharePoint = selectedTools.includes('sharepoint') || selectedTools.includes('onedrive');
      if (effectiveSharePoint) {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
        const [spRes, odRes] = await Promise.all([
          axios.get(`${apiUrl}/auth/sharepoint/status`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/auth/onedrive/status`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const hasSp = spRes.data?.connected;
        const hasOd = odRes.data?.connected;
        if (!hasSp && !hasOd) {
          // Both disconnected - remove from selection and proceed (don't block for stale state)
          effectiveSharePoint = false;
          setUserEnabledTools((prev) => {
            const next = prev.filter((id) => id !== 'sharepoint' && id !== 'onedrive');
            saveEnabledToolsToApi(next);
            if (setUser && user) {
              setUser({ ...user, preferences: { ...user.preferences, enabledTools: next } });
            }
            return next;
          });
        }
      }

      // Check if we need to use a vision-capable model
      const hasImageFiles = selectedFiles.some(file => file.type.startsWith('image/'));
      let finalProvider = selectedProvider;
      let finalModel = selectedModel;
      
      if (hasImageFiles) {
        console.log('Desktop - Image files detected, checking vision capability...');
        const fallbackResult = await checkVisionFallback(selectedProvider, selectedModel, true);
        
        if (fallbackResult.usingFallback) {
          finalProvider = fallbackResult.provider;
          finalModel = fallbackResult.model;
          console.log(`Desktop - Using vision fallback: ${finalProvider}/${finalModel}`);
        } else if (fallbackResult.error) {
          console.error('Desktop - Vision capability error:', fallbackResult.error);
        }
      }

      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      if (input.trim()) {
        formData.append('context', input);
      }

      // Use the final provider/model (potentially fallback)
      if (finalProvider && finalModel) {
        formData.append('provider', finalProvider);
        formData.append('model', finalModel);
      }

      // Add tool selection
      if (selectedTools.includes('search')) formData.append('forceWebSearch', 'true');
      if (selectedTools.includes('create-image')) formData.append('useImageGeneration', 'true');
      if (selectedTools.includes('hubspot')) formData.append('useHubSpotMcp', 'true');
      if (selectedTools.includes('google-drive')) formData.append('useGoogleDrive', 'true');
      if (selectedTools.includes('gmail')) formData.append('useGmail', 'true');
      if (selectedTools.includes('google-calendar')) formData.append('useGoogleCalendar', 'true');
      if (selectedTools.includes('outlook-calendar')) formData.append('useOutlookCalendar', 'true');
      if (selectedTools.includes('outlook-mail')) formData.append('useOutlook', 'true');
      if (selectedTools.includes('github')) formData.append('useGitHub', 'true');
      if (selectedTools.includes('notion')) formData.append('useNotion', 'true');
      if (effectiveSharePoint) formData.append('useSharePoint', 'true');

      // Add hyper accuracy mode flag
      if (hyperAccuracy) {
        formData.append('hyperAccuracy', 'true');
        console.log('🎯 Using Hyper Accuracy mode for file processing');
      } else {
        console.log('⚡ Using Optimized mode for file processing');
      }

      // 🔥 CLEAR THE CHAT INPUT IMMEDIATELY WHEN SENDING (BEFORE UPLOAD)
      setSelectedFiles([]);
      setFileDataUrls([]);
      setInput('');
      setMentionState(createMentionState());
      setLastImprovedPrompt(null); // Clear last improved prompt when sending files
      setHyperAccuracy(false); // Reset hyper accuracy toggle
      clearFallbackInfo(); // Clear fallback info before submission
      
      // Clear suggestions cache since user patterns may have changed
      clearSuggestionsCache();

      // Update localStorage to clear input but keep model and tool selection
      const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        input: '',
        selectedProvider: currentState.selectedProvider || selectedProvider,
        selectedModel: currentState.selectedModel || selectedModel,
        selectedTools: selectedTools,
        lastImprovedPrompt: null
      }));

      await handleFileUpload(formData);
    } catch (error) {
      console.error('Error submitting files:', error);
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileDataUrls(prev => prev.filter((_, i) => i !== index));
    
    // Reset hyperAccuracy if no documents remain
    const remainingFiles = selectedFiles.filter((_, i) => i !== index);
    const hasRemainingDocs = remainingFiles.some(file => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.pdf') || fileName.match(/\.(docx?|txt|md|rtf)$/);
    });
    
    if (!hasRemainingDocs) {
      setHyperAccuracy(false);
    }
    
    // Show suggestions immediately if this was the last file and conditions are met
    if (suggestionsEnabled && remainingFiles.length === 0 && !input.trim() && !chatId && !hasMessages && suggestions.length > 0) {
      setSuggestionsVisible(true);
    }
  };

  // Helper function to check if files are large (>1MB for PDFs, >5MB for other docs)
  const hasLargeFiles = useMemo(() => {
    return selectedFiles.some(file => {
      const isPDF = file.name.toLowerCase().endsWith('.pdf');
      const isDoc = file.name.toLowerCase().match(/\.(docx?|txt|md|rtf)$/);
      
      if (isPDF) {
        return file.size > 1024 * 1024; // 1MB for PDFs
      } else if (isDoc) {
        return file.size > 5 * 1024 * 1024; // 5MB for other documents
      }
      return false;
    });
  }, [selectedFiles]);

  // Helper function to check if files are documents (not just images)
  const hasDocumentFiles = useMemo(() => {
    return selectedFiles.some(file => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.pdf') || 
             fileName.match(/\.(docx?|txt|md|rtf)$/);
    });
  }, [selectedFiles]);

  // Handle Google Drive document selection
  const handleGoogleDriveDocumentSelect = (documentData) => {
    // Create a file-like object for Google Docs
    const googleDocFile = new File(
      [documentData.content], 
      documentData.name + '.txt',
      { type: 'text/plain' }
    );
    
    // Add metadata to track it's from Google Drive
    googleDocFile.googleDriveData = {
      id: documentData.document.id,
      name: documentData.name,
      url: documentData.url,
      modifiedTime: documentData.document.modifiedTime,
      type: 'google-drive'
    };

    // Add to selected files
    setSelectedFiles(prev => [...prev, googleDocFile]);
    
    // Create data URL for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setFileDataUrls(prev => [...prev, event.target.result]);
    };
    reader.readAsDataURL(googleDocFile);
    
    // DO NOT clear the tool selection - keep it persistent as requested
    // setSelectedTool(null);
    
    // Focus the textarea for context input
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Handle model selection
  const handleModelSelect = (provider, model) => {
    console.log(`Selected model: ${provider}/${model}`);
    setSelectedProvider(provider);
    setSelectedModel(model);
    setIsModelDropdownOpen(false);
    
    // Save selection to localStorage immediately to ensure persistence
    const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...currentState,
      selectedProvider: provider,
      selectedModel: model,
      selectedTools: currentState.selectedTools || [],
      lastImprovedPrompt: currentState.lastImprovedPrompt || null
    }));
    
    // Notify parent component if callback is provided
    if (onModelSelect) {
      onModelSelect(provider, model);
    }
  };
  
  // Add toggle dropdown function that logs what's happening
  const toggleModelDropdown = () => {
    console.log('Model dropdown toggled');
    console.log('Current state:', {
      isLoading,
      hasError: !!error,
      modelsCount: availableModels.length,
      selectedProvider,
      selectedModel,
      token: localStorage.getItem('token') ? 'available' : 'missing'
    });
    
    const newIsOpen = !isModelDropdownOpen;
    setIsModelDropdownOpen(newIsOpen);
    
    // Calculate position when opening
    if (newIsOpen) {
      const position = calculateDropdownPosition(modelButtonRef, showTooltipsAbove);
      setModelDropdownPosition(position);
    }
  };

  // Function to retry fetching models
  const retryFetchModels = async () => {
    console.log('Manual retry of model fetch');
    try {
      setIsLoading(true);
      setError('');
      
      // Use the default endpoint since it doesn't require auth
      const url = getApiUrl('models/default');
      console.log(`Trying default endpoint at ${url}`);
      
      const response = await axios.get(url);
      console.log('Response:', response.data);
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setAvailableModels(response.data);
        setSelectedProvider(response.data[0].provider);
        setSelectedModel(response.data[0].model);
        console.log('Successfully loaded models from default endpoint');
      } else {
        setError('No models available');
      }
    } catch (err) {
      console.error('Error in retry:', err);
      setError('Failed to load models');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to try fetching from default endpoint as a fallback
  const tryDefaultEndpoint = () => {
    console.log('Attempting to fetch directly from default endpoint');
    // Instead of hardcoding models, try fetching from the default endpoint again
    const fetchDefaultModels = async () => {
      try {
        setIsLoading(true);
        const fallbackUrl = getApiUrl('models/default');
        console.log(`Directly trying fallback: ${fallbackUrl}`);
        
        const response = await axios.get(fallbackUrl);
        
        if (Array.isArray(response.data) && response.data.length > 0) {
          console.log(`Got ${response.data.length} models from default endpoint`);
          setAvailableModels(response.data);
          setSelectedProvider(response.data[0].provider);
          setSelectedModel(response.data[0].model);
          setError('Unable to load models');
        } else {
          console.error('Default endpoint returned no models');
          setError('Unable to load models');
        }
      } catch (error) {
        console.error('Failed to fetch default models:', error);
        setError('Unable to load models');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDefaultModels();
  };

  // Simplify dropdown rendering by displaying models directly
  // Helper function to get provider logo component
  const getProviderLogo = (provider) => {
    const logoProps = { className: "w-4 h-4 mr-1.5 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" };
    
    switch (provider.toLowerCase()) {
      case 'openai':
        return <OpenAILogo {...logoProps} />;
      case 'anthropic':
        return <ClaudeLogo {...logoProps} />;
      case 'google':
        return <GeminiLogo {...logoProps} />;
      default:
        return null;
    }
  };

  const renderModelDropdown = () => {
    if (isLoading) {
      return (
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-[#999999] [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
          Loading models...
        </div>
      );
    }
    
    // Show error message if present
    if (error) {
      return (
        <div className="flex flex-col">
          <div className="px-3 py-2 text-sm text-red-500 dark:text-red-400">
            {error}
          </div>
       
        </div>
      );
    }
    
    // Group models by provider
    const providers = {};
    // Filter out specific models that shouldn't be shown in the dropdown
    const hiddenModels = ['chatgpt-image-latest', 'o4-mini-deep-research'];
    
    availableModels.forEach(model => {
      // Skip models that should be hidden from the dropdown
      if (hiddenModels.includes(model.model)) {
        return;
      }
      
      const provider = model.provider || 'unknown';
      if (!providers[provider]) {
        providers[provider] = [];
      }
      providers[provider].push(model);
    });
    
    // If no providers after grouping, show a message
    if (Object.keys(providers).length === 0) {
      return (
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-[#999999] [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
          No models available
        </div>
      );
    }

    const midusPickerRows = buildMidusPickerRows(availableModels);
    const midusRowIdSet = new Set(midusPickerRows.map((r) => r.id));

    return (
      <>
        {midusPickerRows.length > 0 && (
          <div className="mb-2 [@media(max-height:800px)]:mb-1.5 [@media(max-height:700px)]:mb-1">
            <div className="px-3 py-1 mb-1 text-[0.825rem] font-regular font-poppins tracking-tight text-black dark:text-white flex items-center [@media(max-height:800px)]:px-2.5 [@media(max-height:800px)]:py-0.5 [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:px-2 [@media(max-height:700px)]:py-0.5 [@media(max-height:700px)]:text-[11px]">
              <BMLogoBlack className="w-[1.25rem] h-[1.25rem] mr-1.5 dark:hidden [@media(max-height:800px)]:w-4 [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:w-3.5 [@media(max-height:700px)]:h-3.5" />
              <BMLogoWhite className="w-[1.25rem] h-[1.25rem] mr-1.5 hidden dark:block [@media(max-height:800px)]:w-4 [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:w-3.5 [@media(max-height:700px)]:h-3.5" />
              Midus
            </div>
            {midusPickerRows.map((row) => (
              <button
                key={row.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm transition-colors rounded-lg [@media(max-height:800px)]:px-2.5 [@media(max-height:800px)]:py-1.5 [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:px-2 [@media(max-height:700px)]:py-1 [@media(max-height:700px)]:text-[11px] ${
                  selectedProvider === row.provider && selectedModel === row.model
                    ? 'bg-[#e1e1e1] dark:bg-[#282828] text-[#181818] dark:text-white'
                    : 'text-gray-500 hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-gray-200 dark:hover:bg-[#222222]'
                }`}
                onClick={() => handleModelSelect(row.provider, row.model)}
                style={{ fontFamily: 'var(--chat-font-family)' }}
              >
                <div className="flex items-center justify-between">
                  <span>{row.displayName}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {(() => {
          const providerOrder = [];
          const sortedProviders = {};
          
          availableModels.forEach(model => {
            if (hiddenModels.includes(model.model)) {
              return;
            }
            const rowId = `${model.provider}:${model.model}`;
            if (midusRowIdSet.has(rowId)) {
              return;
            }
            
            const provider = model.provider || 'unknown';
            if (!providerOrder.includes(provider)) {
              providerOrder.push(provider);
              sortedProviders[provider] = [];
            }
            sortedProviders[provider].push(model);
          });
          
          if (providerOrder.length === 0) return null;

          return (
            <>
              <div className="h-px bg-[#e0e0e0] dark:bg-[#2a2a2a] mx-3 my-3 [@media(max-height:800px)]:mx-2.5 [@media(max-height:800px)]:my-2 [@media(max-height:700px)]:mx-2 [@media(max-height:700px)]:my-1.5"></div>
              {providerOrder.map((provider) => (
                <div key={provider} className="mb-2 last:mb-0 [@media(max-height:800px)]:mb-1.5 [@media(max-height:700px)]:mb-1">
                  <div className="px-3 py-1 text-xs font-semibold text-black dark:text-white uppercase flex items-center [@media(max-height:800px)]:px-2.5 [@media(max-height:800px)]:py-0.5 [@media(max-height:800px)]:text-[11px] [@media(max-height:700px)]:px-2 [@media(max-height:700px)]:py-0.5 [@media(max-height:700px)]:text-[10px]">
                    {getProviderLogo(provider)}
                    {provider.charAt(0).toUpperCase() + provider.slice(1)}
                  </div>
                  {sortedProviders[provider].map((model) => (
                    <button
                      key={`${provider}-${model.model}`}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm transition-colors rounded-lg [@media(max-height:800px)]:px-2.5 [@media(max-height:800px)]:py-1.5 [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:px-2 [@media(max-height:700px)]:py-1 [@media(max-height:700px)]:text-[11px] ${
                        selectedProvider === provider && selectedModel === model.model
                          ? 'bg-[#e1e1e1] dark:bg-[#282828] text-[#181818] dark:text-white'
                          : 'text-gray-500 hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-gray-200 dark:hover:bg-[#222222]'
                      }`}
                      onClick={() => handleModelSelect(provider, model.model)}
                      style={{ fontFamily: 'var(--chat-font-family)' }}
                    >
                      <div className="flex items-center justify-between">
                        <span>{formatModelName(model.model, model)}</span>
                        {model.isPro && (
                          <span className="ml-2 text-xs font-semibold bg-gradient-to-r from-purple-600 to-blue-500 text-transparent bg-clip-text [@media(max-height:800px)]:text-[10px] [@media(max-height:700px)]:text-[9px]">
                            PRO
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </>
          );
        })()}
      </>
    );
  };

  // Format model name for display
  const formatModelName = (modelName, modelData) => {
    // If a custom display name is provided by the backend, use it
    if (modelData && modelData.displayName) {
      return modelData.displayName;
    }
    
    if (!modelName) return '';
    
    // If it's already a nice name like "Claude 3 Opus", return it as is
    if (/^[A-Z]/.test(modelName) || modelName.includes(' ')) {
      return modelName;
    }
    
    // Format based on model prefix
    if (modelName.startsWith('gpt-')) {
      // For OpenAI models - convert gpt-4-turbo to GPT-4 Turbo
      return modelName
        .replace('gpt-', 'GPT-')
        .replace(/-([0-9])/g, ' $1')
        .replace(/-([a-z])/g, (_, letter) => ' ' + letter.toUpperCase());
    } else if (modelName.startsWith('claude-')) {
      // For Claude models
      return modelName
        .replace('claude-', 'Claude ')
        .replace(/-([0-9])/g, ' $1')
        .replace(/-([a-z])/g, ' $1')
        .replace(/\b([a-z])/g, letter => letter.toUpperCase())
        .replace(/-?\d{8}$/, ''); // Remove date suffixes
    } else if (modelName.startsWith('gemini-')) {
      // For Gemini models
      return modelName
        .replace('gemini-', 'Gemini ')
        .replace(/-([0-9])/g, ' $1')
        .replace(/-([a-z])/g, ' $1')
        .replace(/\b([a-z])/g, letter => letter.toUpperCase());
    }
    
    // Generic formatting for other models
    return modelName
      .replace(/-([0-9])/g, ' $1')
      .replace(/-([a-z])/g, ' $1')
      .replace(/\b([a-z])/g, letter => letter.toUpperCase());
  };

  // Get display name for current selection
  const getSelectedModelDisplay = () => {
    if (isLoading) return "Loading models...";
    
    if (!selectedProvider || !selectedModel) {
        return (
        <div className="flex font-poppins tracking-tight items-center text-gray-600 dark:text-gray-400 [&_img]:opacity-70">
          <img src={fullLogoUrls.light} alt="Logo" className="h-5 w-auto dark:hidden [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:h-3.5" />
          <img src={fullLogoUrls.dark} alt="Logo" className="h-5 w-auto hidden dark:block [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:h-3.5" />
        </div>
      );
    }
    
    // Find the selected model to get its display name if available
    const selectedModelData = availableModels.find(m => 
      m.provider === selectedProvider && m.model === selectedModel
    );
    
    // Format the model name to be more readable - pass the model data for custom display name
    const formattedModel = formatModelName(selectedModel, selectedModelData);
    
    const isPro = selectedModelData?.isPro;
    
    return (
      <div className="flex items-center">
        {getProviderLogo(selectedProvider)}
        <span>{formattedModel}</span>
        {isPro && (
          <span className="ml-2 text-xs font-semibold bg-gradient-to-r from-purple-600 to-blue-500 text-transparent bg-clip-text">
            PRO
          </span>
        )}
      </div>
    );
  };

  // Handle tool toggle - add/remove from persisted selection, save to DB
  const handleToolToggle = (tool) => {
    if (isSuperBucketMode && tool !== 'superbucket') return;

    setUserEnabledTools(prev => {
      const isOn = prev.includes(tool);
      if (tool === 'superbucket') {
        if (isOn) {
          setIsSuperBucketMode(false);
          const next = prev.filter(t => t !== 'superbucket');
          saveEnabledToolsToApi(next);
          return next;
        }
        setIsSuperBucketMode(true);
        const next = ['superbucket'];
        saveEnabledToolsToApi(next);
        return next;
      }
      if (isOn) {
        const next = prev.filter(t => t !== tool);
        saveEnabledToolsToApi(next);
        return next;
      }
      setIsSuperBucketMode(false);
      let next;
      if (tool === 'deep-research') {
        // deep-research turns off search and create-image
        next = [...prev.filter(t => t !== 'superbucket' && !DEEP_RESEARCH_EXCLUDES.includes(t)), tool];
      } else if (DEEP_RESEARCH_EXCLUDES.includes(tool)) {
        // search/create-image turns off deep-research
        next = [...prev.filter(t => t !== 'superbucket' && t !== 'deep-research'), tool];
      } else {
        next = [...prev.filter(t => t !== 'superbucket'), tool];
      }
      saveEnabledToolsToApi(next);
      if (tool === 'google-drive') {
        setTimeout(() => setIsGoogleDriveModalOpen(true), 0);
      }
      return next;
    });
  };

  // Calculate optimal dropdown position and height
  const calculateDropdownPosition = (buttonRef, preferredShowAbove = false, forFixed = false) => {
    if (!buttonRef?.current) return { showAbove: preferredShowAbove, maxHeight: 300, top: 0, left: 0, bottom: undefined };
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownPadding = 16; // Padding from viewport edges
    const dropdownOffset = 12; // Gap between button and dropdown - balanced spacing
    
    // Calculate available space above and below the button
    const spaceAbove = buttonRect.top - dropdownPadding;
    const spaceBelow = viewportHeight - buttonRect.bottom - dropdownPadding;
    
    // Start with the preferred direction
    let showAbove = preferredShowAbove;
    
    // Check if there's enough space in the preferred direction
    const minRequiredSpace = 120; // Minimum space needed for dropdown
    const preferredSpace = preferredShowAbove ? spaceAbove : spaceBelow;
    const alternativeSpace = preferredShowAbove ? spaceBelow : spaceAbove;
    
    // If preferred direction doesn't have enough space, use alternative if it's better
    if (preferredSpace < minRequiredSpace && alternativeSpace > preferredSpace) {
      showAbove = !preferredShowAbove;
    }
    
    // Calculate optimal max height based on final position
    const availableSpace = showAbove ? spaceAbove - dropdownOffset : spaceBelow - dropdownOffset;
    const maxHeight = Math.min(300, Math.max(120, availableSpace)); // Min 120px, max 300px
    
    const result = { showAbove, maxHeight };
    if (forFixed) {
      result.top = showAbove ? undefined : buttonRect.bottom + dropdownOffset;
      result.left = buttonRect.left;
      result.bottom = showAbove ? viewportHeight - buttonRect.top + dropdownOffset : undefined;
    }
    return result;
  };

  const toggleToolsDropdown = () => {
    const newIsOpen = !isToolsDropdownOpen;
    setIsToolsDropdownOpen(newIsOpen);
    if (newIsOpen) setIsConnectorsDropdownOpen(false);
    if (newIsOpen) {
      const position = calculateDropdownPosition(toolsButtonRef, showTooltipsAbove, true);
      setToolsDropdownPosition(position);
    }
    if (isToolsDropdownOpen) setHoveredTool(null);
  };

  // State to track which tool is being hovered
  const [hoveredTool, setHoveredTool] = useState(null);
  
  // State to track dropdown positions
  const [modelDropdownPosition, setModelDropdownPosition] = useState({ showAbove: false, maxHeight: 300 });
  const [toolsDropdownPosition, setToolsDropdownPosition] = useState({ showAbove: false, maxHeight: 300 });
  const [connectorsDropdownPosition, setConnectorsDropdownPosition] = useState({ showAbove: false, maxHeight: 300 });

  const CORE_TOOLS = [
    { id: 'deep-research', name: 'Deep Research', icon: LuTelescope, description: 'Advanced reasoning and multi-step analysis with citations', isPremium: true },
    { id: 'search', name: 'Search the web', icon: FiGlobe, description: 'Search for current information online' },
    { id: 'create-image', name: 'Create an image', icon: FaRegImage, description: 'Generate images using AI' }
  ];

  const CONNECTOR_APPS = [
    { id: 'google-drive', name: 'Google Drive', src: driveSvg, description: 'Access files and documents in Drive' },
    { id: 'gmail', name: 'Gmail', src: gmailSvg, description: 'Search, read, and send emails' },
    { id: 'google-calendar', name: 'Google Calendar', src: googleCalendarSvg, description: 'View and manage calendar events' },
    { id: 'outlook-calendar', name: 'Outlook Calendar', src: outlookCalendarSvg, description: 'View and manage Outlook calendar events' },
    { id: 'outlook-mail', name: 'Outlook Mail', src: outlookSvg, description: 'Search, read, and send Outlook emails' },
    { id: 'sharepoint', name: 'SharePoint', src: sharepointSvg, description: 'Search and read files from SharePoint sites' },
    { id: 'onedrive', name: 'OneDrive', src: onedriveSvg, description: 'Search and read files from your OneDrive' },
    { id: 'hubspot', name: 'HubSpot', icon: HubSpotIcon, description: 'Query contacts, deals, companies, and more from HubSpot CRM' },
    { id: 'github', name: 'GitHub', icon: FaGithub, description: 'List repos, search issues, read files, and manage pull requests' },
    { id: 'notion', name: 'Notion', icon: SiNotion, description: 'Search pages, read notes, and query databases in your workspace' }
  ];

  const renderToolRow = (tool) => {
    const IconComponent = tool.icon;
    const iconSrc = tool.src;
    const disabledBySuperBucket = isSuperBucketMode ? tool.id !== 'superbucket' : tool.id === 'superbucket' && hasMessages;
    const disabledByDeepResearch = selectedTools.includes('deep-research') && DEEP_RESEARCH_EXCLUDES.includes(tool.id);
    const isDisabled = disabledBySuperBucket || disabledByDeepResearch;
    const isHovered = hoveredTool === tool.id;
    const isOn = selectedTools.includes(tool.id);
    return (
      <div key={tool.id} className="relative">
        <div
          className={`chat-input-dropdown-item w-full text-left px-3 py-2.5 text-sm flex items-center ${
            isDisabled ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed' : 'text-gray-900 dark:text-white cursor-pointer'
          }`}
          onMouseEnter={() => setHoveredTool(tool.id)}
          onMouseLeave={() => setHoveredTool(null)}
        >
          <button
            type="button"
            onClick={() => { if (!isDisabled) handleToolToggle(tool.id); }}
            disabled={isDisabled}
            className="flex flex-1 items-center min-w-0 text-left"
            style={{ fontFamily: 'var(--chat-font-family)' }}
          >
            {iconSrc ? (
              <span className="w-4 h-4 mr-2 flex-shrink-0 flex items-center justify-center">
                <img src={iconSrc} alt={tool.name} className="w-full h-full object-contain" />
              </span>
            ) : (
              <IconComponent className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: tool.id === 'hubspot' ? '#FF7A59' : tool.id === 'github' ? (darkMode ? '#F0F0F0' : '#181717') : tool.id === 'notion' ? (darkMode ? '#F0F0F0' : '#000000') : 'currentColor' }} />
            )}
            <span className={`flex-1 ${tool.id === 'deep-research' ? 'font-medium' : ''}`}>{tool.name}</span>
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={isOn}
            disabled={isDisabled}
            onClick={(e) => { e.stopPropagation(); if (!isDisabled) handleToolToggle(tool.id); }}
            className={`ml-2 flex-shrink-0 w-8 h-4 rounded-full transition-colors relative ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${isOn ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-[#444]'}`}
          >
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white dark:bg-[#1a1a1a] shadow-sm transition-transform ${isOn ? 'translate-x-4 left-0.5' : 'translate-x-0 left-0.5'}`} />
          </button>
        </div>
        {isDisabled && isHovered && (
          <div className="absolute left-full ml-2 top-0 opacity-100 transition-opacity duration-200 pointer-events-none z-50">
            <div className="px-2 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg">
              {disabledByDeepResearch ? 'Turn off Deep Research to use this tool' : isSuperBucketMode && tool.id !== 'superbucket' ? 'Other tools are disabled in super bucket chats' : 'Create a new chat to query SuperBucket'}
              <div className="absolute -left-[6px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tl-[4px] border-t border-l border-[#dbdbdb] dark:border-[#2a2a2a] transform rotate-45" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const CORE_TOOL_IDS = ['deep-research', 'search', 'create-image'];
  const DEEP_RESEARCH_EXCLUDES = ['search', 'create-image']; // deep-research is mutually exclusive with these
  const CONNECTOR_IDS = ['google-drive', 'gmail', 'google-calendar', 'outlook-calendar', 'outlook-mail', 'sharepoint', 'onedrive', 'hubspot', 'github', 'notion'];

  const renderToolsDropdown = () => {
    return (
      <>
        {CORE_TOOLS.map(renderToolRow)}
        {/* SuperBucket toggle - commented out
        {isUserAdmin && (
          <>
            <div className="border-t border-gray-200 dark:border-[#282828] my-1.5" />
            {[{ id: 'superbucket', name: 'SuperBucket', icon: FaSuperpowers, description: 'Query organizational intelligence' }].map(renderToolRow)}
          </>
        )}
        */}
      </>
    );
  };

  const renderConnectorsDropdown = () => {
    const connectorSelected = selectedTools.filter(id => CONNECTOR_IDS.includes(id));
    const connectedOnly = CONNECTOR_APPS.filter(app => connectedConnectors[app.id]);
    const hasConnectedConnectors = connectedOnly.length > 0;

    const addConnectorsButton = (
      <button
        type="button"
        onClick={() => {
          setIsConnectorsDropdownOpen(false);
          openConnectToolsModal();
        }}
        className="chat-input-dropdown-item w-full text-left px-3 py-2.5 text-sm text-gray-900 dark:text-white flex items-center gap-2"
      >
        <TbPlug className="w-[17px] h-[17px] text-current" />
        Add connectors
      </button>
    );

    const footerSection = hasConnectedConnectors ? (
      <div className="border-t border-gray-200 dark:border-[#282828] mt-1 pt-1 space-y-0.5">
        {addConnectorsButton}
        <button
          type="button"
          onClick={() => {
            setIsConnectorsDropdownOpen(false);
            window.dispatchEvent(new CustomEvent('open-account-modal', { detail: { tab: 'connectors' } }));
          }}
          className="chat-input-dropdown-item w-full text-left px-3 py-2.5 text-sm text-gray-900 dark:text-white flex items-center gap-2"
        >
          Manage connectors
        </button>
      </div>
    ) : (
      addConnectorsButton
    );

    if (isConnectorsLoading) {
      return (
        <>
          <div className="px-3 py-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2 animate-pulse">
                <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-[#444] flex-shrink-0" />
                <div className="h-3 flex-1 rounded bg-gray-300 dark:bg-[#444] max-w-[120px]" />
                <div className="w-8 h-4 rounded-full bg-gray-300 dark:bg-[#444] flex-shrink-0" />
              </div>
            ))}
          </div>
          {footerSection}
        </>
      );
    }

    return (
      <>
        {connectedOnly.map(renderToolRow)}
        {footerSection}
      </>
    );
  };

  const toggleConnectorsDropdown = () => {
    const newIsOpen = !isConnectorsDropdownOpen;
    setIsConnectorsDropdownOpen(newIsOpen);
    if (newIsOpen) setIsToolsDropdownOpen(false);
    if (newIsOpen) {
      const position = calculateDropdownPosition(connectorsContainerRef, showTooltipsAbove, true);
      setConnectorsDropdownPosition(position);
    }
    if (isConnectorsDropdownOpen) setHoveredTool(null);
  };

  // Shared fetch for connector status (used on mount and when dropdown opens)
  const fetchConnectorStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
    try {
      const [driveRes, gmailRes, calendarRes, hubspotRes, outlookRes, outlookCalendarRes, sharepointRes, onedriveRes, githubRes, notionRes] = await Promise.allSettled([
        axios.get(`${apiUrl}/auth/google-drive/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/google-drive/status?connector=gmail`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/google-drive/status?connector=calendar`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/hubspot/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/outlook/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/outlook-calendar/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/sharepoint/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/onedrive/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/github/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/auth/notion/status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setConnectedConnectors({
        'google-drive': driveRes.status === 'fulfilled' && driveRes.value?.data?.connected,
        'gmail': gmailRes.status === 'fulfilled' && gmailRes.value?.data?.connected,
        'google-calendar': calendarRes.status === 'fulfilled' && calendarRes.value?.data?.connected,
        'hubspot': hubspotRes.status === 'fulfilled' && hubspotRes.value?.data?.connected,
        'outlook-mail': outlookRes.status === 'fulfilled' && outlookRes.value?.data?.connected,
        'outlook-calendar': outlookCalendarRes.status === 'fulfilled' && outlookCalendarRes.value?.data?.connected,
        sharepoint: sharepointRes.status === 'fulfilled' && sharepointRes.value?.data?.connected,
        onedrive: onedriveRes.status === 'fulfilled' && onedriveRes.value?.data?.connected,
        github: githubRes.status === 'fulfilled' && githubRes.value?.data?.connected,
        notion: notionRes.status === 'fulfilled' && notionRes.value?.data?.connected,
      });
      setHasConnectorsBeenFetched(true);
    } catch (e) {
      console.error('Error fetching connector status:', e);
    } finally {
      setIsConnectorsLoading(false);
    }
  }, []);

  // Fetch connector status on mount (for icon stack display) and when connectors dropdown opens (cached)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (hasConnectorsBeenFetched) {
      setIsConnectorsLoading(false);
      return;
    }
    setIsConnectorsLoading(true);
    fetchConnectorStatus();
  }, [hasConnectorsBeenFetched, fetchConnectorStatus]);

  // Invalidate connectors cache when ConnectToolsModal closes (user may have connected something)
  const handleConnectToolsModalClose = () => {
    setShowConnectToolsModal(false);
    setHasConnectorsBeenFetched(false);
    try {
      localStorage.removeItem('connectToolsModalOpen');
      const params = new URLSearchParams(window.location.search);
      params.delete('connectTools');
      const newSearch = params.toString();
      window.history.replaceState({}, '', newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
    } catch {}
  };

  const openConnectToolsModal = () => {
    setShowConnectToolsModal(true);
    try {
      localStorage.setItem('connectToolsModalOpen', '1');
      const params = new URLSearchParams(window.location.search);
      params.set('connectTools', '1');
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    } catch {}
  };

  // Listen for connector changes from AccountModal (disconnect/connect) - refresh tool selector and invalidate cache
  useEffect(() => {
    const handler = (e) => {
      const disconnectedIds = e.detail?.disconnectedIds || [];
      const connectedIds = e.detail?.connectedIds || [];
      setHasConnectorsBeenFetched(false);
      // Optimistically update connectedConnectors so icon stack updates immediately
      if (disconnectedIds.length > 0) {
        setConnectedConnectors((prev) => {
          const next = { ...prev };
          disconnectedIds.forEach((id) => { next[id] = false; });
          return next;
        });
      }
      if (connectedIds.length > 0) {
        setConnectedConnectors((prev) => {
          const next = { ...prev };
          connectedIds.forEach((id) => { next[id] = true; });
          return next;
        });
      }
      if (disconnectedIds.length > 0) {
        setUserEnabledTools((prev) => {
          const next = prev.filter((id) => !disconnectedIds.includes(id));
          saveEnabledToolsToApi(next);
          if (setUser && user) {
            setUser({ ...user, preferences: { ...user.preferences, enabledTools: next } });
          }
          return next;
        });
      }
      if (connectedIds.length > 0) {
        setUserEnabledTools((prev) => {
          const toAdd = connectedIds.filter((id) => !prev.includes(id));
          if (toAdd.length === 0) return prev;
          const next = [...prev, ...toAdd];
          saveEnabledToolsToApi(next);
          if (setUser && user) {
            setUser({ ...user, preferences: { ...user.preferences, enabledTools: next } });
          }
          return next;
        });
      }
    };
    window.addEventListener('connectors-updated', handler);
    return () => window.removeEventListener('connectors-updated', handler);
  }, [saveEnabledToolsToApi, setUser, user]);

  // Invalidate connectors cache when AccountModal closes (opened from Sidebar or elsewhere)
  useEffect(() => {
    const handler = () => setHasConnectorsBeenFetched(false);
    window.addEventListener('account-modal-closed', handler);
    return () => window.removeEventListener('account-modal-closed', handler);
  }, []);

  const TOOL_ICON_CONFIG = useMemo(() => ({
    'deep-research': { Icon: LuTelescope },
    'search': { Icon: FiGlobe },
    'create-image': { Icon: FaRegImage },
    'google-drive': { src: driveSvg },
    'gmail': { src: gmailSvg },
    'google-calendar': { src: googleCalendarSvg },
    'outlook-calendar': { src: outlookCalendarSvg },
    'outlook-mail': { src: outlookSvg },
    'sharepoint': { src: sharepointSvg },
    'onedrive': { src: onedriveSvg },
    'hubspot': { Icon: HubSpotIcon },
    'github': { Icon: FaGithub },
    'notion': { Icon: SiNotion },
    'superbucket': { Icon: FaSuperpowers }
  }), []);

  const coreSelected = useMemo(() => selectedTools.filter(id => CORE_TOOL_IDS.includes(id) || id === 'superbucket'), [selectedTools]);
  const displayConnectors = useMemo(() => {
    // Always show enabled connectors so user sees their tools; connected status is shown in dropdown
    return selectedTools.filter(id => CONNECTOR_IDS.includes(id));
  }, [selectedTools]);

  const getToolsDisplay = () => (
      <div className="flex items-center gap-1.5">
        <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center overflow-hidden [&>svg]:w-full [&>svg]:h-full [&>svg]:min-w-full [&>svg]:min-h-full [&>svg]:block">
          <ToolsIcon />
        </span>
        {coreSelected.length > 0 && (
          <div className="flex items-center -space-x-1.5">
            {coreSelected.map((id, i) => {
              const config = TOOL_ICON_CONFIG[id];
              if (!config) return null;
              return (
                <div key={id} className="w-5 h-5 rounded-full flex items-center justify-center bg-white dark:bg-[#282828] border border-gray-200 dark:border-[#333] flex-shrink-0 relative p-0.5 text-gray-500 dark:text-[#999999]" style={{ zIndex: coreSelected.length - i }} title={id.replace(/-/g, ' ')}>
                  {config.src ? <img src={config.src} alt={id} className="w-full h-full object-contain opacity-70 dark:opacity-80" /> : <config.Icon className="w-3 h-3" style={{ color: id === 'hubspot' ? '#FF7A59' : id === 'github' ? (darkMode ? '#F0F0F0' : '#181717') : id === 'notion' ? (darkMode ? '#F0F0F0' : '#000000') : undefined }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );

  const getConnectorsDisplay = () => (
      <div className="flex items-center gap-1.5">
        {displayConnectors.length === 0 ? (
          <TbPlug className="w-[17px] h-[17px] flex-shrink-0 text-current" title="Connectors" />
        ) : (
          <div className="flex items-center -space-x-1.5">
            {displayConnectors.map((id, i) => {
              const config = TOOL_ICON_CONFIG[id];
              if (!config) return null;
              return (
                <div key={id} className="w-5 h-5 rounded-full flex items-center justify-center bg-white dark:bg-[#282828] border border-gray-200 dark:border-[#333] flex-shrink-0 relative p-0.5 text-gray-500 dark:text-[#999999]" style={{ zIndex: displayConnectors.length - i }} title={id.replace(/-/g, ' ')}>
                  {config.src ? <img src={config.src} alt={id} className="w-full h-full object-contain block opacity-70 dark:opacity-80" /> : <config.Icon className="w-3 h-3" style={{ color: id === 'hubspot' ? '#FF7A59' : id === 'github' ? (darkMode ? '#F0F0F0' : '#181717') : id === 'notion' ? (darkMode ? '#F0F0F0' : '#000000') : undefined }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );

  // Clear suggestions cache (useful when user patterns change)
  const clearSuggestionsCache = () => {
    try {
      localStorage.removeItem('chat_suggestions_cache');
      console.log('Cleared suggestions cache');
    } catch (error) {
      console.error('Error clearing suggestions cache:', error);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    console.log('Suggestion clicked:', suggestion);
    
    // Set the input to the suggestion's prompt
    setInput(suggestion.prompt);
    
    // Hide suggestions with proper animation
    setSuggestionsVisible(false);
    
    // Focus the textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
    
    // Adjust textarea height for the new content
    setTimeout(() => {
      adjustTextareaHeight();
    }, 0);
  };

  // Handle suggestions hide
  const handleSuggestionsHide = () => {
    setSuggestions([]);
    setSuggestionsVisible(false);
  };

  // Show fallback info to user
  const renderVisionFallbackInfo = () => {
    if (!fallbackInfo || !fallbackInfo.fallbackModel) return null;

    return (
      <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-blue-800">
              <strong>Model switched for image processing:</strong>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {fallbackInfo.message}
            </p>
          </div>
          <button
            onClick={clearFallbackInfo}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Helper function to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color) return false;
    
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if light (luminance > 0.5)
    return luminance > 0.5;
  };

  // Function to generate a darker secondary color based on the primary color (same logic as Sidebar.js)
  const getSecondaryColorFromPrimary = (hexColor) => {
    if (!hexColor) return null;
    
    // Remove the # if present
    const hex = hexColor.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Check if we're in dark mode
    const isDarkMode = document.documentElement.classList.contains('dark');
    
    let secondaryR, secondaryG, secondaryB;
    
    if (isDarkMode) {
      // In dark mode, generate much darker versions for better contrast (reduce each component by about 80-85%)
      secondaryR = Math.max(0, Math.floor(r * 0.15));
      secondaryG = Math.max(0, Math.floor(g * 0.15));
      secondaryB = Math.max(0, Math.floor(b * 0.15));
    } else {
      // In light mode, generate much lighter versions (increase towards white with low opacity effect)
      // This creates a subtle tint of the color rather than a dark background
      secondaryR = Math.min(255, Math.floor(r + (255 - r) * 0.85));
      secondaryG = Math.min(255, Math.floor(g + (255 - g) * 0.85));
      secondaryB = Math.min(255, Math.floor(b + (255 - b) * 0.85));
    }
    
    // Convert back to hex
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(secondaryR)}${toHex(secondaryG)}${toHex(secondaryB)}`;
  };

  // Helper function to get send button text color based on background
  const getSendButtonTextColor = () => {
    if (controlsDisabled && showTypingIndicator && typingUserColor) {
      // When typing indicator is shown, use color that contrasts with user color
      return isLightColor(typingUserColor) ? '#000000' : '#ffffff';
    } else {
      // Default: white text in light mode (black bg), black text in dark mode (white bg)
      return document.documentElement.classList.contains('dark') ? '#000000' : '#ffffff';
    }
  };

  // Helper function to get send button background color
  const getSendButtonBackgroundColor = () => {
    if (controlsDisabled && showTypingIndicator && typingUserColor) {
      return typingUserColor;
    } else if (controlsDisabled || (disabled && isInitialChatLoad)) {
      return '#666666'; // Gray when disabled or loading
    } else {
      // Default: black in light mode, white in dark mode
      return document.documentElement.classList.contains('dark') ? '#ffffff' : '#000000';
    }
  };

  // Helper function to clean markdown from text
  const cleanMarkdown = (text) => {
    if (!text) return text;
    
    return text
      // Remove bold and italic formatting
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // **bold** -> bold
      .replace(/\*([^*]+)\*/g, '$1')      // *italic* -> italic
      .replace(/_{2,}([^_]+)_{2,}/g, '$1') // __bold__ -> bold
      .replace(/_([^_]+)_/g, '$1')        // _italic_ -> italic
      // Remove headers
      .replace(/^#{1,6}\s+/gm, '')        // # Header -> Header
      // Remove code blocks and inline code
      .replace(/```[^`]*```/g, '')        // ```code``` -> (remove)
      .replace(/`([^`]+)`/g, '$1')        // `code` -> code
      // Remove links
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) -> text
      // Remove other markdown symbols
      .replace(/^[-*+]\s+/gm, '')         // - item -> item
      .replace(/^\d+\.\s+/gm, '')         // 1. item -> item (but keep some numbering)
      // Clean up extra whitespace
      .replace(/\n{3,}/g, '\n\n')         // Multiple newlines -> double newlines
      .trim();
  };

  // Handle prompt improvement
  const handlePromptImprovement = async () => {
    if (!input.trim() || isImprovingPrompt || controlsDisabled) return;
    
    setIsImprovingPrompt(true);
    
    // Start the clearing animation
    setIsTextareaClearing(true);
    
    // Wait for clearing animation to complete, then show shimmer
    setTimeout(() => {
      setShowImprovingShimmer(true);
    }, 300);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(
        getApiUrl('api/chat/improve-prompt'),
        {
          prompt: input.trim(),
          model: 'gpt-4o-mini',
          provider: 'openai'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.improvedPrompt) {
        // Clean markdown formatting before setting the input
        const cleanedPrompt = cleanMarkdown(response.data.improvedPrompt);
        
        // Hide shimmer and start revealing animation
        setShowImprovingShimmer(false);
        setIsTextareaClearing(false);
        setIsTextareaRevealing(true);
        
        // Set the improved prompt
        setInput(cleanedPrompt);
        setLastImprovedPrompt(cleanedPrompt); // Track the last improved prompt
        
        // Update localStorage immediately to persist the improvement
        const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          ...currentState,
          input: cleanedPrompt,
          lastImprovedPrompt: cleanedPrompt
        }));
        
        adjustTextareaHeight();
        
        // Focus the textarea after improvement and clean up animation states
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
          // Clean up reveal animation state
          setTimeout(() => {
            setIsTextareaRevealing(false);
          }, 1500); // Match the shimmer duration
        }, 100);
      } else {
        console.error('Failed to improve prompt:', response.data.error);
        // Reset states on error
        setShowImprovingShimmer(false);
        setIsTextareaClearing(false);
        setIsTextareaRevealing(false);
      }
    } catch (error) {
      console.error('Error improving prompt:', error);
      // Reset states on error
      setShowImprovingShimmer(false);
      setIsTextareaClearing(false);
      setIsTextareaRevealing(false);
    } finally {
      setIsImprovingPrompt(false);
    }
  };

  // Helper function to get control button colors when someone is typing
  const getControlButtonColors = () => {
    if (showTypingIndicator && typingUserColor && isProjectChat) {
      return {
        color: typingUserColor,
        hoverColor: typingUserColor,
        hoverBg: `${typingUserColor}20` // 20% opacity of the typing user's color
      };
    }
    return null; // Use default styling
  };

  const controlButtonSizeClass = 'p-2';
  // Helper function to get control button class with typing colors
  const getControlButtonClass = (baseClass, isActive = false) => {
    const typingColors = getControlButtonColors();
    if (typingColors) {
      // When someone is typing, use a completely different class structure with opacity dimming
      return `p-2 ${controlButtonSizeClass} rounded-[10px] transition-colors opacity-60 cursor-not-allowed ${isActive ? 'bg-[#e1e1e1] dark:bg-[#282828]' : 'hover:bg-[#e7e7e7] dark:hover:bg-[#282828]'}`;
    }
    return `${baseClass} ${controlButtonSizeClass}`;
  };

  // Helper function to get control button style with typing colors
  const getControlButtonStyle = (isActive = false) => {
    const typingColors = getControlButtonColors();
    if (typingColors) {
      return {
        color: typingColors.color
      };
    }
    return {};
  };

  // Determine if we should show typing indicator (only in project chats)
  const showTypingIndicator = isProjectChat && isOtherUserTyping && typingUserName;
  
  // Determine if controls should be disabled (when someone else is typing in project chat, usage is blocked, payment failed, or system status blocks chat)
  const controlsDisabled = showTypingIndicator || isUsageBlocked || hasPaymentFailure || isChatBlocked;
  const micDisabled = controlsDisabled || voiceTranscribing;

  const shouldShowMentionHighlight = !showTypingIndicator && (hasColoredMention || mentionState.active);
  const highlightedInputHtml = useMemo(() => {
    if (!shouldShowMentionHighlight) {
      return '';
    }

    const caretPosition = mentionCaretIndex ?? (textareaRef.current ? textareaRef.current.selectionStart : input.length);
    const segments = [];
    let index = 0;

    while (index < input.length) {
      if (mentionState.active && mentionState.startIndex === index && caretPosition >= index) {
        const typedSegment = input.slice(index, caretPosition);
        if (typedSegment) {
          const color = typedSegment.startsWith('@') ? (showTypingIndicator ? undefined : (darkMode ? '#ffffff' : '#000000')) : selectedMentionHighlightColor || userMentionColorHex;
          const sanitizedTyped = escapeHtml(typedSegment);
          segments.push(
            color
              ? `<span style="font-weight:600;color:${color}">${sanitizedTyped}</span>`
              : `<span style="font-weight:600">${sanitizedTyped}</span>`
          );
        }
        if (mentionCompletionSuffix) {
          segments.push(
            `<span style="font-weight:600;color:${mentionSuffixColor}">${escapeHtml(mentionCompletionSuffix)}</span>`
          );
        }
        index = caretPosition;
        continue;
      }

      const remaining = input.slice(index);
      const mentionMatch = remaining.match(/^@[A-Za-z0-9._-]+/);
      if (mentionMatch) {
        const mentionText = mentionMatch[0];
        const color = mentionColorMap[mentionText.toLowerCase()];
        const sanitizedMention = escapeHtml(mentionText);
        segments.push(
          color
            ? `<span style="font-weight:600;color:${color}">${sanitizedMention}</span>`
            : sanitizedMention
        );
        index += mentionText.length;
        continue;
      }

      const char = input[index];
      if (char === '\n') {
        segments.push('<br />');
      } else {
        segments.push(escapeHtml(char));
      }
      index += 1;
    }

    return segments.join('');
  }, [
    input,
    shouldShowMentionHighlight,
    mentionState.active,
    mentionState.startIndex,
    mentionCaretIndex,
    selectedMentionHighlightColor,
    userMentionColorHex,
    mentionCompletionSuffix,
    mentionSuffixColor,
    mentionColorMap
  ]);



  // Debug logging for typing indicator
  useEffect(() => {
    console.log('💬 ChatInput typing props:', {
      isProjectChat,
      isOtherUserTyping,
      typingUserName,
      typingUserColor,
      showTypingIndicator,
      controlsDisabled
    });
  }, [isProjectChat, isOtherUserTyping, typingUserName, typingUserColor, showTypingIndicator, controlsDisabled]);

  // Helper function specifically for model selector button class
  const getModelSelectorClass = (isActive = false) => {
    const typingColors = getControlButtonColors();
    if (typingColors) {
      // When someone is typing, use typing color with simple class structure and opacity dimming
      return `flex items-center px-2 py-2 text-sm rounded-[10px] transition-colors opacity-60 cursor-not-allowed [@media(max-height:800px)]:px-1.5 [@media(max-height:800px)]:py-1.5 [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:px-1 [@media(max-height:700px)]:py-1 [@media(max-height:700px)]:text-[11px] ${
        isActive ? 'bg-[#e1e1e1] dark:bg-[#282828]' : 'hover:bg-[#e7e7e7] dark:hover:bg-[#282828]'
      }`;
    }
    // Default classes when not typing
    return `flex items-center px-2 py-2 text-sm rounded-[10px] transition-colors [@media(max-height:800px)]:px-1.5 [@media(max-height:800px)]:py-1.5 [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:px-1 [@media(max-height:700px)]:py-1 [@media(max-height:700px)]:text-[11px] ${
      controlsDisabled 
        ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed' 
        : `text-gray-500 hover:text-[#181818] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-gray-200 dark:hover:bg-[#282828] ${isActive ? 'text-[#0A0A0A] bg-[#e1e1e1] dark:text-white dark:bg-[#282828]' : ''}`
    }`;
  };

  // Determine tooltip position based on whether we have messages or not
  // When there are messages, chat input is at bottom - show tooltips above
  // When no messages (greeting), chat input is centered - show tooltips below
  const showTooltipsAbove = hasMessages;
  
  // Get tooltip positioning classes
  const getTooltipPositionClasses = () => {
    if (showTooltipsAbove) {
      return {
        container: "absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+8px)] opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform group-hover:-translate-y-1",
        arrow: "absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-br-[4px] border-b border-r border-[#C3C3C3] dark:border-[#2a2a2a] transform rotate-45"
      };
    } else {
      return {
        container: "absolute left-1/2 -translate-x-1/2 top-[calc(100%+12px)] opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform group-hover:translate-y-1",
        arrow: "absolute -top-[6px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tl-[4px] border-t border-l border-[#C3C3C3] dark:border-[#2a2a2a] transform rotate-45"
      };
    }
  };

  const tooltipClasses = getTooltipPositionClasses();

  // Handle improve prompt button animation
  useEffect(() => {
    const shouldShow = input.trim().length >= 20;
    
    if (shouldShow && !shouldRenderImproveBtn) {
      // Show button with animation
      setShouldRenderImproveBtn(true);
      setIsImproveBtnAnimating(true);
      setTimeout(() => {
        setIsImproveBtnAnimating(false);
      }, 50);
    } else if (!shouldShow && shouldRenderImproveBtn) {
      // Hide button with animation
      setIsImproveBtnAnimating(true);
      setTimeout(() => {
        setShouldRenderImproveBtn(false);
        setIsImproveBtnAnimating(false);
      }, 300);
    }
  }, [input, shouldRenderImproveBtn]);

  // Handle model selector animation when tools are selected/deselected
  useEffect(() => {
    const hideForTools = ['search', 'deep-research', 'create-image'];
    const shouldHideModelSelector = selectedTools.some(t => hideForTools.includes(t));
    
    if (shouldHideModelSelector && shouldRenderModelSelector) {
      // Hide model selector when tool is selected (except SuperBucket and HubSpot)
      setIsModelSelectorAnimating(true);
      setTimeout(() => {
        setShouldRenderModelSelector(false);
        setIsModelSelectorAnimating(false);
      }, 300);
    } else if (!shouldHideModelSelector && !shouldRenderModelSelector) {
      // Show model selector when tool is deselected or SuperBucket/HubSpot is selected
      setShouldRenderModelSelector(true);
      setIsModelSelectorAnimating(true);
      setTimeout(() => {
        setIsModelSelectorAnimating(false);
      }, 50);
    }
  }, [selectedTools, shouldRenderModelSelector]);

  // Note: Stealth routing for tools (like web search → Claude, create-image → chatgpt-image-latest) 
  // is handled in the backend. The frontend just sends the tool selection flag.

  // Check if current chat is a super bucket chat and set tool accordingly
  useEffect(() => {
    const checkSuperBucketChat = async () => {
      console.log('SUPERBUCKET CHECK TRIGGERED:', { 
        chatId, 
        isUserAdmin, 
        currentTools: selectedTools, 
        currentMode: isSuperBucketMode 
      });

      if (!chatId || !isUserAdmin) {
        if (isSuperBucketMode) {
          setIsSuperBucketMode(false);
        }
        return;
      }

      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
        const token = localStorage.getItem('token');
        
        console.log('🌐 Making API call to:', `${API_URL}/chat/${chatId}`);
        
        const response = await fetch(`${API_URL}/chat/${chatId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📡 API Response status:', response.status);
        
        if (response.ok) {
          const chatData = await response.json();
          
          console.log('FULL CHAT DATA RECEIVED:', JSON.stringify(chatData, null, 2));
          console.log('SPECIFIC CHECK:', { 
            'chatData.chat exists': !!chatData.chat,
            'chatData.chat.isSuperBucketChat': chatData.chat?.isSuperBucketChat,
            'typeof': typeof chatData.chat?.isSuperBucketChat,
            'condition result': !!(chatData.chat && chatData.chat.isSuperBucketChat)
          });
          
          // Check if this chat was created with the superbucket tool
          if (chatData.chat && chatData.chat.isSuperBucketChat) {
            setIsSuperBucketMode(true);
          } else {
            setIsSuperBucketMode(false);
          }
        } else {
          console.error('API call failed:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('💥 Error checking super bucket chat status:', error);
      }
    };

    checkSuperBucketChat();
  }, [chatId, isUserAdmin]);

  // Check if user has admin/owner role on component mount
  useEffect(() => {
    const checkAdminStatus = () => {
      console.log('ADMIN CHECK - Checking admin status...', { user, NODE_ENV: process.env.NODE_ENV });
      // 🧪 In test mode, check if impersonated user is admin
      if (process.env.NODE_ENV === 'development') {
        const testOrgMode = localStorage.getItem('devTestOrgMode');
        const activeTestUser = localStorage.getItem('devActiveTestUser');
        
        if (testOrgMode === 'true' && activeTestUser) {
          // If impersonating Test Admin, they have admin access
          const isTestAdmin = activeTestUser === 'Test Admin';
          setIsUserAdmin(isTestAdmin);
          console.log('🧪 Test user admin check:', { activeTestUser, isTestAdmin });
          console.log('ADMIN STATUS SET TO:', isTestAdmin);
          return;
        }
      }
      
      // Regular admin check for production
      const isAdmin = isOrganizationAdmin();
      console.log('REGULAR ADMIN CHECK:', isAdmin);
      setIsUserAdmin(isAdmin);
      console.log('ADMIN STATUS SET TO:', isAdmin);
    };
    
    checkAdminStatus();
    
    // Listen for test user changes
    const handleTestUserChange = (event) => {
      if (event.detail?.userName) {
        const isTestAdmin = event.detail.userName === 'Test Admin';
        setIsUserAdmin(isTestAdmin);
        console.log('🧪 Test user changed, updating admin status:', { 
          userName: event.detail.userName, 
          isTestAdmin 
        });
      } else {
        // Back to regular user
        const isAdmin = isOrganizationAdmin();
        setIsUserAdmin(isAdmin);
      }
    };
    
    window.addEventListener('testUserChanged', handleTestUserChange);
    return () => window.removeEventListener('testUserChanged', handleTestUserChange);
  }, []);

  // Get the display name to use in placeholder (prefer nickname over full company name)
  const displayName = organizationNickname || companyName;

  useEffect(() => {
    const getPlaceholderText = () => {
      if (showTypingIndicator) {
        return `${typingUserName} is prompting...`;
      } else if (selectedFiles.length > 0) {
        return "Add context...";
      } else if (selectedTools.includes('superbucket')) {
        return "Ask anything about your organization...";
      } else if (isProjectChat) {
        return "Send a message or @midus";
      } else if (hasMessages) {
        return "Ask a follow up...";
      } else {
        return "Ask Midus...";
      }
    };
    setCurrentPlaceholder(getPlaceholderText());
  }, [selectedTools, showTypingIndicator, typingUserName, selectedFiles.length, displayName, isProjectChat, hasMessages]);

  return (
    <div className="sticky bottom-0 left-0 right-0 w-full justify-center px-4 pb-4 flex">
      <div ref={outerContainerRef} className="w-full max-w-[800px] relative">
        {/* Error message display - above the chat input container */}
        {externalError && (
          <div className="mb-3">
            <div className="flex items-center gap-2 bg-[#FFF5F1] dark:bg-[#2A1F1F] border border-[#f46a4f] dark:border-[#8B3A3A] text-[#E67F25] dark:text-[#FF8C69] px-4 py-3 rounded-2xl w-full [@media(max-height:800px)]:px-3 [@media(max-height:800px)]:py-2.5 [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:py-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <span className="flex-1 text-sm [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:text-[12px]">{externalError}</span>
              <button 
                onClick={onErrorRetry} 
                className="flex-shrink-0 hover:text-[#D16A13] dark:hover:text-[#FF6B47] transition-colors"
                title="Retry failed message"
              >
                <LuRefreshCcw className="w-5 h-5" />
              </button>
            </div>
          </div>
                )}

        {/* ChatGPT-style suggestions - hide for critical notifications */}
        {suggestionsEnabled && (
          <ChatSuggestions 
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            isVisible={suggestionsVisible && !showPaymentWarning && !isUsageBlocked && !isChatBlocked}
            onHide={handleSuggestionsHide}
          />
        )}
        
        {/* Payment failure notification - below suggestions, above chat input (highest priority) */}
        {showPaymentWarning && paymentData && (
          <div className="mb-3">
            <PaymentFailureNotification
              user={user}
              paymentData={paymentData}
              isVisible={showPaymentWarning}
              isAdminOrOwner={isPaymentAdmin}
            />
          </div>
        )}
        
        {/* System status notification - below payment, above usage */}
        {isChatBlocked && (
          <div className="mb-3">
            <SystemStatusNotification
              systemStatus={systemStatus}
              blockingReason={blockingReason}
              isVisible={isChatBlocked}
            />
          </div>
        )}
        
        {/* Usage limit notification - below suggestions, above chat input */}
        {((isAdminOrOwner && showNotification) || isUsageBlocked) && notificationData && (
          <div className="mb-3">
            <UsageLimitNotification
              user={user}
              usagePercentage={notificationData.usagePercentage}
              usageAmount={notificationData.usageAmount}
              monthlyAllowance={notificationData.monthlyAllowance}
              usageBasedPricingEnabled={notificationData.usageBasedPricingEnabled}
              subscriptionPlan={notificationData.subscriptionPlan}
              onDismiss={handleNotificationDismiss}
              onToggleUsageBasedPricing={handleToggleUsageBasedPricing}
              isVisible={showNotification || isUsageBlocked}
              isUsageBlocked={isUsageBlocked}
              creditRefreshDate={notificationData.creditRefreshDate}
              onAcknowledgeBlocked={acknowledgeBlockingNotification}
            />
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="relative z-20 w-full transition-all duration-300 ease-out">
            <div 
              ref={chatInputRef}
              className={`relative flex flex-col border transition-[border-color,transform,border-radius,box-shadow] ease-in-out ${selectedFiles.length > 0 ? 'duration-300' : 'duration-500'} focus-within:border-[#C4C7CC] dark:focus-within:border-[#363636] ${
                isDragOver 
                  ? 'border-[#737373] dark:border-[#525252] bg-[#F9FAFB] dark:bg-[#0a0a0a] scale-[1.005]' 
                  : showTypingIndicator && typingUserColor
                    ? ''  // No border or background class when typing - will be handled by inline style
                    : selectedTools.includes('superbucket')
                      ? 'border-[#F3B59D] dark:border-[#F3B59D] bg-[#F9FAFB] dark:bg-[#181818]'
                      : 'border-[#E5E7EB] dark:border-[#282828] bg-[#F9FAFB] dark:bg-[#181818]'
              }`}
              style={{
                borderRadius: `${getDynamicBorderRadius(containerHeight)}px`,
                ...getCornerShapeSquircleInlineStyle(),
                // Superbucket glow
                ...(selectedTools.includes('superbucket') && {
                  boxShadow: isTextareaFocused
                    ? '0 0 30px rgba(243,181,157,0.4), 0 0 60px rgba(243,181,157,0.3)'
                    : '0 0 20px rgba(243,181,157,0.2), 0 0 40px rgba(243,181,157,0.15)'
                }),
                // Apply typing user's colors when someone is typing
                ...(showTypingIndicator && typingUserColor && !isDragOver && {
                  borderColor: typingUserColor,
                  backgroundColor: getSecondaryColorFromPrimary(typingUserColor)
                })
              }}
            >
            {mentionState.active && (
              <div className="pointer-events-none absolute bottom-[calc(100%+10px)] left-0 w-full flex justify-start">
                <div className="pointer-events-auto w-full max-w-[420px] rounded-2xl border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0F0F0F] shadow-xl">
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleMentionSelection({ mentionHandle: 'midus' });
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 transition-colors rounded-t-2xl ${
                      mentionHighlightedIndex === 0
                        ? 'bg-gray-100 dark:bg-[#1f1f1f]'
                        : 'hover:bg-gray-50 dark:hover:bg-[#181818]'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2a2a2a] flex items-center justify-center overflow-hidden">
                      <img
                        src={darkMode ? fullLogoUrls.dark : fullLogoUrls.light}
                        alt="Logo"
                        className="w-5 h-5 object-contain"
                        onError={(event) => {
                          event.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-semibold text-black dark:text-white">@midus</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{organizationNickname || companyName || 'Company'} AI</span>
                    </div>
                  </button>
                  <div className="h-px bg-gray-200 dark:bg-[#2a2a2a]" />
                  <div className="max-h-60 overflow-y-auto">
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member, idx) => {
                        const isActive = mentionHighlightedIndex === idx + 1;
                        const initials = member.name?.trim()?.charAt(0)?.toUpperCase() || 'U';
                        return (
                        <button
                            key={member.id || idx}
                            type="button"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleMentionSelection(member);
                            }}
                          className={`w-full flex items-center gap-3 px-3 py-2 transition-colors ${idx === filteredMembers.length - 1 ? 'rounded-b-2xl' : ''} ${
                              isActive
                                ? 'bg-gray-100 dark:bg-[#1f1f1f]'
                                : 'hover:bg-gray-50 dark:hover:bg-[#181818]'
                            }`}
                          >
                            {member.avatarUrl ? (
                              <img
                                src={member.avatarUrl}
                                alt={member.name}
                                className="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-[#2a2a2a]"
                              />
                            ) : (
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                                style={{ backgroundColor: member.colorHex || '#E5E7EB', color: '#111111' }}
                              >
                                {initials}
                              </div>
                            )}
                            <div className="flex flex-col items-start">
                              <span className="text-sm font-medium text-black dark:text-white">
                                {member.name}
                              </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              @{member.mentionHandle}
                            </span>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                        No project members found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Drag overlay */}
            {isDragOver && (
              <div 
                className="absolute inset-0 bg-black/[0.07] dark:bg-white/[0.08] flex items-center justify-center z-10 pointer-events-none"
                style={{
                  borderRadius: `${getDynamicBorderRadius(containerHeight)}px`,
                  ...getCornerShapeSquircleInlineStyle()
                }}
              >
                <div className="text-[#525252] dark:text-[#737373] text-sm font-medium [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:text-xs">
                  Drop files to upload
                </div>
              </div>
            )}

            {/* Main content - grid animates whole chat input together; slower collapse so it feels as smooth as expand */}
            <div 
              className={`grid transition-[grid-template-rows] ease-in-out ${selectedFiles.length > 0 ? 'duration-300' : 'duration-500'}`}
              style={{ gridTemplateRows: selectedFiles.length > 0 ? '1fr auto' : '0fr auto' }}
            >
              {/* File preview - images on first row, files on second row (only when files selected) */}
              <div className="overflow-hidden min-h-0">
                {selectedFiles.length > 0 && (
                  <div className="flex flex-col gap-2 px-3 pt-3 pb-1">
                    {(() => {
                      const imageEntries = selectedFiles
                        .map((file, index) => ({ file, index }))
                        .filter(({ file }) => file.type?.startsWith('image/'));
                      const fileEntries = selectedFiles
                        .map((file, index) => ({ file, index }))
                        .filter(({ file }) => !file.type?.startsWith('image/'));
                      return (
                        <>
                          {imageEntries.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {imageEntries.map(({ file, index }) => (
                                <FileContextDisplay
                                  key={`img-${file.name}-${index}`}
                                  file={file}
                                  context={index === 0 ? fileContext : ''}
                                  onRemove={() => handleRemoveFile(index)}
                                  dataUrl={fileDataUrls[index]}
                                />
                              ))}
                            </div>
                          )}
                          {fileEntries.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {fileEntries.map(({ file, index }) => (
                                <FileContextDisplay
                                  key={`file-${file.name}-${index}`}
                                  file={file}
                                  context={index === 0 ? fileContext : ''}
                                  onRemove={() => handleRemoveFile(index)}
                                  dataUrl={fileDataUrls[index]}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Textarea row */}
              <div className="w-full min-h-[44px] [@media(max-height:800px)]:min-h-[40px] [@media(max-height:700px)]:min-h-[36px]">
              {showImprovingShimmer ? (
                <div className="flex items-center p-4 w-full min-h-[44px] [@media(max-height:800px)]:p-3 [@media(max-height:700px)]:p-2 [@media(max-height:800px)]:min-h-[40px] [@media(max-height:700px)]:min-h-[36px]" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  <SkeletonTheme 
                    baseColor={document.documentElement.classList.contains('dark') ? "#999999" : "#9ca3af"} 
                    highlightColor={document.documentElement.classList.contains('dark') ? "#bbbbbb" : "#d1d5db"}
                  >
                    <div 
                      style={{
                        width: '140px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <div
                        style={{
                          width: '140px',
                          height: '16px',
                          maskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                            <svg width="140" height="16" viewBox="0 0 140 16" xmlns="http://www.w3.org/2000/svg">
                              <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">Improving prompt...</text>
                            </svg>
                          `)}")`,
                          WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                            <svg width="140" height="16" viewBox="0 0 140 16" xmlns="http://www.w3.org/2000/svg">
                              <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">Improving prompt...</text>
                            </svg>
                          `)}")`,
                          maskSize: 'contain',
                          WebkitMaskSize: 'contain',
                          maskRepeat: 'no-repeat',
                          WebkitMaskRepeat: 'no-repeat',
                          maskPosition: 'left center',
                          WebkitMaskPosition: 'left center'
                        }}
                      >
                        <div style={{ width: '100%', height: '100%' }}>
                          <Skeleton 
                            width={140}
                            height={16}
                            style={{ 
                              borderRadius: 0,
                              display: 'block',
                              margin: 0,
                              padding: 0,
                              lineHeight: '1',
                              verticalAlign: 'top'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </SkeletonTheme>
                </div>
              ) : (
                <div 
                  className="w-full overflow-hidden chat-input-container relative"
                  style={{
                    borderTopLeftRadius: `${getDynamicBorderRadius(containerHeight)}px`,
                    borderTopRightRadius: `${getDynamicBorderRadius(containerHeight)}px`,
                    ...getCornerShapeSquircleInlineStyle()
                  }}
                >
                  {shouldShowMentionHighlight && (
                    <div
                      className="absolute inset-0 px-4 pt-4 pb-4 text-sm text-black dark:text-white pointer-events-none whitespace-pre-wrap z-0 [@media(max-height:800px)]:px-3 [@media(max-height:800px)]:pt-3 [@media(max-height:800px)]:pb-3 [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:pt-2.5 [@media(max-height:700px)]:pb-2.5 [@media(max-height:700px)]:text-[12px]"
                      style={{
                        fontFamily: 'var(--chat-font-family)',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        minHeight: 'var(--chat-input-min-height)'
                      }}
                      aria-hidden="true"
                      dangerouslySetInnerHTML={{ __html: highlightedInputHtml || '&nbsp;' }}
                    />
                  )}
                  {voiceTranscribing && !isRecording && (
                    <div className="absolute inset-0 pointer-events-none z-20">
                      <div
                        className="w-full px-4 pt-4 text-sm [@media(max-height:800px)]:px-3 [@media(max-height:800px)]:pt-3 [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:pt-2.5 [@media(max-height:700px)]:text-[12px]"
                        style={{ fontFamily: 'var(--chat-font-family)', minHeight: 'var(--chat-input-min-height)' }}
                      >
                        <SkeletonTheme
                          baseColor={document.documentElement.classList.contains('dark') ? "#666666" : "#6b7280"}
                          highlightColor={document.documentElement.classList.contains('dark') ? "#999999" : "#9ca3af"}
                        >
                          <div style={{ width: '140px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <div
                              style={{
                                width: '140px',
                                height: '16px',
                                maskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                                  <svg width="140" height="16" viewBox="0 0 140 16" xmlns="http://www.w3.org/2000/svg">
                                    <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">Transcribing...</text>
                                  </svg>
                                `)}")`,
                                WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                                  <svg width="140" height="16" viewBox="0 0 140 16" xmlns="http://www.w3.org/2000/svg">
                                    <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">Transcribing...</text>
                                  </svg>
                                `)}")`,
                                maskSize: 'contain',
                                WebkitMaskSize: 'contain',
                                maskRepeat: 'no-repeat',
                                WebkitMaskRepeat: 'no-repeat',
                                maskPosition: 'left center',
                                WebkitMaskPosition: 'left center'
                              }}
                            >
                              <div style={{ width: '100%', height: '100%' }}>
                                <Skeleton width={140} height={16} style={{ borderRadius: 0, display: 'block', margin: 0, padding: 0, lineHeight: '1', verticalAlign: 'top' }} />
                              </div>
                            </div>
                          </div>
                        </SkeletonTheme>
                      </div>
                    </div>
                  )}
                  {(isRecording || FORCE_SHOW_RECORDING_UI) && (
                    <div className="absolute inset-0 flex items-center w-full min-w-0 px-2 z-20 overflow-hidden">
                      <VoiceRecordingIndicator
                        durationLabel={FORCE_SHOW_RECORDING_UI ? '1:23' : formatDuration(recordingDuration)}
                        levels={FORCE_SHOW_RECORDING_UI ? SAMPLE_WAVEFORM_LEVELS : levels}
                        darkMode={darkMode}
                      />
                    </div>
                  )}
                  <textarea
                    ref={textareaRef}
                    value={showTypingIndicator ? `${typingUserName} is prompting...` : input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onSelect={handleSelectionChange}
                    onFocus={() => setIsTextareaFocused(true)}
                    onBlur={() => setIsTextareaFocused(false)}
                    disabled={controlsDisabled}
                    placeholder={currentPlaceholder}
                    className={`w-full bg-transparent px-4 pt-4 text-sm text-black dark:text-white border-gray-300 focus:outline-none focus:ring-0 resize-none [@media(max-height:800px)]:px-3 [@media(max-height:800px)]:pt-3 [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:pt-2.5 [@media(max-height:700px)]:text-[12px] ${
                      controlsDisabled 
                        ? 'cursor-not-allowed opacity-60' 
                        : ''
                    } ${
                      isTextareaClearing ? 'textarea-clearing' : ''
                    } ${
                      isTextareaRevealing ? 'improved-prompt-reveal' : ''
                    } ${shouldShowMentionHighlight ? 'relative z-10' : ''} ${
                      (isRecording || voiceTranscribing || FORCE_SHOW_RECORDING_UI) ? 'opacity-0 pointer-events-none' : ''
                    }`}
                    rows={1}
                    style={{
                      minHeight: 'var(--chat-input-min-height)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontFamily: 'var(--chat-font-family)',
                      // Hide scrollbar until 50px scroll
                      scrollbarWidth: showScrollbar ? 'thin' : 'none',
                      msOverflowStyle: showScrollbar ? 'auto' : 'none',
                      // Apply typing user's color to text when showing typing message
                      ...(showTypingIndicator && typingUserColor && {
                        color: typingUserColor,
                        '--placeholder-color': typingUserColor
                      }),
                      ...(shouldShowMentionHighlight && {
                        color: 'transparent',
                        caretColor: darkMode ? '#ffffff' : '#111111'
                      })
                    }}
                    {...spellCheck.getSpellCheckProps()}
                  />
                </div>
              )}
              {/* Custom styling for scrollbar visibility */}
              <style>
                {`
                  .chat-input-container {
                    --chat-input-min-height: 44px;
                  }
                  .chat-input-container textarea::-webkit-scrollbar {
                    width: ${showScrollbar ? '12px' : '0px'};
                    background: transparent;
                  }
                  .chat-input-container textarea::-webkit-scrollbar-track {
                    background: ${showScrollbar ? 'transparent' : 'transparent'};
                    border-radius: 6px;
                  }
                  .chat-input-container textarea::-webkit-scrollbar-thumb {
                    background: ${showScrollbar ? '#d1d5db' : 'transparent'} !important;
                    border-radius: 6px;
                    border: none;
                  }
                  .chat-input-container textarea::-webkit-scrollbar-thumb:hover {
                    background: ${showScrollbar ? '#c4c4c4' : 'transparent'} !important;
                  }
                  .dark .chat-input-container textarea::-webkit-scrollbar-thumb {
                    background: ${showScrollbar ? '#999999' : 'transparent'} !important;
                    border-radius: 6px;
                    border: none;
                  }
                  .dark .chat-input-container textarea::-webkit-scrollbar-thumb:hover {
                    background: ${showScrollbar ? '#b3b3b3' : 'transparent'} !important;
                  }
                `}
              </style>

              {/* Custom styling for disabled placeholder */}
              {controlsDisabled && !showTypingIndicator && (
                <style>
                  {`
                    textarea::placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    textarea::-webkit-input-placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    textarea::-moz-placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    textarea:-ms-input-placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    .dark textarea::placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    .dark textarea::-webkit-input-placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    .dark textarea::-moz-placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                    .dark textarea:-ms-input-placeholder {
                      color: #999999 !important;
                      opacity: 0.4 !important;
                    }
                  `}
                </style>
              )}

              {/* Custom styling for colored placeholder */}
              {showTypingIndicator && typingUserColor && (
                <style>
                  {`
                    textarea::placeholder {
                      color: ${typingUserColor} !important;
                      opacity: 1 !important;
                    }
                    textarea::-webkit-input-placeholder {
                      color: ${typingUserColor} !important;
                      opacity: 1 !important;
                    }
                    textarea::-moz-placeholder {
                      color: ${typingUserColor} !important;
                      opacity: 1 !important;
                    }
                    textarea:-ms-input-placeholder {
                      color: ${typingUserColor} !important;
                      opacity: 1 !important;
                    }
                  `}
                </style>
              )}
              
              {/* Custom styling for regular placeholder in dark mode */}
              {!showTypingIndicator && (
                <style>
                  {`
                    .dark textarea::placeholder {
                      color: #999999 !important;
                      opacity: 1 !important;
                    }
                    .dark textarea::-webkit-input-placeholder {
                      color: #999999 !important;
                      opacity: 1 !important;
                    }
                    .dark textarea::-moz-placeholder {
                      color: #999999 !important;
                      opacity: 1 !important;
                    }
                    .dark textarea:-ms-input-placeholder {
                      color: #999999 !important;
                      opacity: 1 !important;
                    }
                  `}
                </style>
              )}
            </div>

            {/* Bottom row - Controls */}
            <div className="chat-input-controls flex items-center justify-between px-1.5 pr-1.5 pb-1.5 [@media(max-height:800px)]:px-1 [@media(max-height:800px)]:pb-1 [@media(max-height:700px)]:px-0.5 [@media(max-height:700px)]:pb-0.5">
              {/* Left side controls */}
              <div className="flex items-center">
                {/* Plus button */}
                <div className="relative group">
                  <button 
                    className={getControlButtonClass(`p-2 rounded-[10px] transition-colors ${
                      controlsDisabled 
                        ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed' 
                        : 'text-gray-500 hover:text-[#0A0A0A] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-white dark:hover:bg-[#282828]'
                    }`)}
                    onClick={controlsDisabled ? undefined : handleFileButtonClick}
                    type="button"
                    disabled={controlsDisabled}
                    style={getControlButtonStyle()}
                  >
                    <span className="w-4 h-4 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3 flex-shrink-0 flex items-center justify-center overflow-hidden [&>svg]:w-full [&>svg]:h-full [&>svg]:min-w-full [&>svg]:min-h-full [&>svg]:block">
                      <ChatInputPlusIcon className="text-inherit" />
                    </span>
                  </button>
                  
                  {!controlsDisabled && (
                    <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform`}>
                      <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                        Upload files and more
                        <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                <input 
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                  multiple
                  disabled={controlsDisabled}
                />

                {/* Ask Midus button */}
                {isProjectChat && handleDiscussionSend && (
                  <div className="relative group ml-0.5">
                    <button
                      type="button"
                      className={getControlButtonClass(`p-2 rounded-[10px] transition-colors ${
                        controlsDisabled 
                          ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed' 
                          : 'text-gray-500 hover:text-[#0A0A0A] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-white dark:hover:bg-[#282828]'
                      }`)}
                      onClick={controlsDisabled ? undefined : () => handleAskMidus()}
                      disabled={controlsDisabled}
                      style={getControlButtonStyle()}
                    >
                      <LuSparkles className="w-4 h-4 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" />
                    </button>

                    {!controlsDisabled && (
                      <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform`}>
                        <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                          Ask Midus
                          <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tools selector - always visible so user can toggle superbucket on/off */}
                {(
                  <div className="relative group ml-0.5">
                    <button
                      ref={toolsButtonRef}
                      type="button"
                      className={getControlButtonClass(`${coreSelected.length > 0 ? 'px-2 py-1.5' : 'p-2'} rounded-[10px] transition-colors ${
                        controlsDisabled 
                          ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed' 
                          : `text-gray-500 hover:text-[#0A0A0A] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-white dark:hover:bg-[#282828] ${isToolsDropdownOpen ? 'text-[#0A0A0A] bg-[#e1e1e1] dark:text-white dark:bg-[#282828]' : ''}`
                      }`, isToolsDropdownOpen)}
                      onClick={controlsDisabled ? undefined : toggleToolsDropdown}
                      disabled={controlsDisabled}
                      style={getControlButtonStyle(isToolsDropdownOpen)}
                    >
                      {getToolsDisplay()}
                    </button>
                  
                  {/* Tools selector tooltip */}
                  {!controlsDisabled && !isToolsDropdownOpen && (
                    <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform`}>
                      <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                        Select tools
                        <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                      </div>
                    </div>
                  )}

                  {isToolsDropdownOpen && !controlsDisabled && createPortal(
                    <div 
                      ref={toolsDropdownRef}
                      className="chat-input-dropdown fixed w-max min-w-[210px] px-1.5 py-0 z-[100] flex flex-col overflow-hidden"
                      style={{
                        top: toolsDropdownPosition.top,
                        left: toolsDropdownPosition.left,
                        bottom: toolsDropdownPosition.bottom
                      }}
                    >
                      <div
                        className="overflow-y-auto flex-1 min-h-0 py-1.5"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch',
                          maxHeight: `${toolsDropdownPosition.maxHeight}px`
                        }}
                      >
                        {renderToolsDropdown()}
                      </div>
                    </div>,
                    document.body
                  )}
                  </div>
                )}

                {/* Prompt Improvement Button */}
                {shouldRenderImproveBtn && (
                  <div className="relative group ml-0.5">
                    <div 
                      className="transition-all duration-300 ease-out overflow-visible"
                      style={{
                        width: input.trim().length >= 20 && !isImproveBtnAnimating ? '30px' : '0px',
                        opacity: input.trim().length >= 20 && !isImproveBtnAnimating ? 1 : 0,
                        transform: input.trim().length >= 20 && !isImproveBtnAnimating ? 'translateX(0)' : 'translateX(-10px)',
                        pointerEvents: isImproveBtnAnimating ? 'none' : 'auto'
                      }}
                    >
                      <button
                        type="button"
                        className={getControlButtonClass(`p-1.5 rounded-[10px] transition-colors relative ${
                          controlsDisabled || isImprovingPrompt || (lastImprovedPrompt && input.trim() === lastImprovedPrompt)
                            ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed'
                            : 'text-gray-500 hover:text-[#0A0A0A] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-white dark:hover:bg-[#282828]'
                        }`)}
                        onClick={controlsDisabled || isImprovingPrompt || (lastImprovedPrompt && input.trim() === lastImprovedPrompt) ? undefined : handlePromptImprovement}
                        disabled={controlsDisabled || isImprovingPrompt || (lastImprovedPrompt && input.trim() === lastImprovedPrompt)}
                        style={getControlButtonStyle()}
                      >
                        {isImprovingPrompt ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-400 rounded-full animate-spin"></div>
                        ) : (
                          <LuSparkles className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    
                    {/* Prompt improvement tooltip */}
                    {!controlsDisabled && (
                      <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform z-50`}>
                        <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                          <span>
                            {(lastImprovedPrompt && input.trim() === lastImprovedPrompt) 
                              ? 'Already improved - make changes to improve again' 
                              : 'Improve prompt'}
                          </span>
                          <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Connectors button - separate from Tools */}
                <div ref={connectorsContainerRef} className="relative group ml-0.5">
                  <button
                    type="button"
                    className={getControlButtonClass(`${displayConnectors.length > 0 ? 'p-1.5' : 'p-[7.5px]'} rounded-[10px] transition-colors ${
                      controlsDisabled
                        ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed'
                        : `text-gray-500 hover:text-[#0A0A0A] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-white dark:hover:bg-[#282828] ${isConnectorsDropdownOpen ? 'text-[#0A0A0A] bg-[#e1e1e1] dark:text-white dark:bg-[#282828]' : ''}`
                    }`, isConnectorsDropdownOpen)}
                    onClick={controlsDisabled ? undefined : toggleConnectorsDropdown}
                    disabled={controlsDisabled}
                    style={getControlButtonStyle(isConnectorsDropdownOpen)}
                  >
                    {getConnectorsDisplay()}
                  </button>
                  {!controlsDisabled && !isConnectorsDropdownOpen && (
                    <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform`}>
                      <div className="chat-input-tooltip pl-2.5 pr-1.5 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg">
                        <div className="flex items-center gap-2">
                          <span>Connectors</span>
                          <span className="border border-current text-gray-600 dark:text-[#999999] text-xs font-medium px-1.5 py-0.5 rounded-md">
                            BETA
                          </span>
                        </div>
                        <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`} />
                      </div>
                    </div>
                  )}
                  {isConnectorsDropdownOpen && !controlsDisabled && createPortal(
                    <div 
                      ref={connectorsDropdownRef}
                      className="chat-input-dropdown fixed w-max min-w-[240px] px-1.5 py-0 z-[100] flex flex-col overflow-hidden"
                      style={{
                        top: connectorsDropdownPosition.top,
                        left: connectorsDropdownPosition.left,
                        bottom: connectorsDropdownPosition.bottom
                      }}
                    >
                      <div
                        className="overflow-y-auto flex-1 min-h-0 py-1.5"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch',
                          maxHeight: `${connectorsDropdownPosition.maxHeight}px`
                        }}
                      >
                        {renderConnectorsDropdown()}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>

                {/* Model selector dropdown - with graceful hide/show animation - COMMENTED OUT */}
                {/* <div 
                  className="relative group ml-1"
                  style={{
                    opacity: shouldRenderModelSelector && !isModelSelectorAnimating ? 1 : 0,
                    transform: shouldRenderModelSelector && !isModelSelectorAnimating ? 'scale(1)' : 'scale(0.95)',
                    transformOrigin: 'left center',
                    transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
                    pointerEvents: isModelSelectorAnimating || !shouldRenderModelSelector ? 'none' : 'auto',
                    visibility: shouldRenderModelSelector ? 'visible' : 'hidden'
                  }}
                >
                    <button
                      ref={modelButtonRef}
                      type="button"
                      className={getModelSelectorClass(isModelDropdownOpen)}
                      onClick={controlsDisabled ? undefined : toggleModelDropdown}
                      style={{ 
                        fontFamily: 'var(--chat-font-family)',
                        ...getControlButtonStyle(isModelDropdownOpen)
                      }}
                      disabled={controlsDisabled}
                    >
                      {getSelectedModelDisplay()}
                      <ChevronDownIcon className="w-4 h-4 ml-1 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" />
                    </button>
                  
                    {!controlsDisabled && !isModelDropdownOpen && (
                      <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform`}>
                        <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                          Change model
                          <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                        </div>
                      </div>
                    )}

                    {isModelDropdownOpen && !controlsDisabled && (
                      <div 
                        ref={modelDropdownRef}
                        className={`chat-input-dropdown absolute ${modelDropdownPosition.showAbove ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+12px)]'} left-0 w-52 bg-[#f2f2f2]/85 dark:bg-[#181818] backdrop-blur-sm rounded-xl shadow-lg border border-gray-300 dark:border-[#282828] py-1.5 px-1.5 z-50 overflow-y-auto [@media(max-height:800px)]:w-48 [@media(max-height:800px)]:py-1 [@media(max-height:800px)]:px-1 [@media(max-height:700px)]:w-44 [@media(max-height:700px)]:py-0.5 [@media(max-height:700px)]:px-0.5`}
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch',
                          maxHeight: `${modelDropdownPosition.maxHeight}px`
                        }}
                      >
                        {renderModelDropdown()}
                      </div>
                    )}
                  </div> */}

                {/* Hyper Accuracy Toggle - Part of left controls */}
                {hasDocumentFiles && (
                  <div className="flex items-center gap-2 text-sm px-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setHyperAccuracy(!hyperAccuracy);
                      }}
                      disabled={controlsDisabled}
                      type="button"
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        hyperAccuracy
                          ? 'bg-black border-black dark:bg-white dark:border-white'
                          : 'bg-transparent border-gray-400 dark:border-gray-500 hover:border-gray-500 dark:hover:border-gray-400'
                      } ${controlsDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {hyperAccuracy && (
                        <CheckIcon className={`w-3 h-3 stroke-[4px] ${
                          hyperAccuracy ? 'text-white dark:text-black' : ''
                        }`} />
                      )}
                    </button>
                    
                    <span 
                      className={`select-none text-xs text-gray-600 dark:text-[#999999] ${
                        controlsDisabled ? 'opacity-50' : 'cursor-pointer'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!controlsDisabled) setHyperAccuracy(!hyperAccuracy);
                      }}
                    >
                      Hyper Accuracy
                    </span>
                    
                    {/* Tooltip */}
                    <div className="relative group">
                      <div className="w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-500 flex items-center justify-center text-xs text-white font-bold cursor-help">
                        ?
                      </div>
                      <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform z-50`}>
                        <div className="w-72 px-4 py-3 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-gray-600 dark:text-[#999999] text-sm relative shadow-lg">
                          <div className="mb-2 font-semibold text-gray-800 dark:text-gray-200">Hyper Accuracy Mode</div>
                          <div className="space-y-1 text-xs leading-relaxed">
                            <div className="flex items-start gap-2">
                              <span className="text-gray-500 dark:text-gray-500 mt-0.5">•</span>
                              <span>Processes and retains entire document for maximum accuracy</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-gray-500 dark:text-gray-500 mt-0.5">•</span>
                              <span>Slower response times</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-gray-500 dark:text-gray-500 mt-0.5">•</span>
                              <span>Increased token usage{hasLargeFiles ? ' (significant for larger documents)' : ''}</span>
                            </div>
                          </div>
                          <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right side controls - z-50 when recording so stop tooltip appears above overlay */}
              <div className={`flex items-center gap-1.5 ${isRecording ? 'relative z-50' : ''}`}>
                {/* Microphone button - fades out when recording */}
                <div className={`relative group transition-opacity duration-200 ${isRecording ? 'opacity-0 pointer-events-none' : ''}`}>
                  <button 
                                            className={getControlButtonClass(`p-2 rounded-[10px] transition-colors ${
                          micDisabled && !isRecording
                            ? 'text-gray-600 dark:text-[#999999] opacity-40 cursor-not-allowed'
                            : isRecording 
                              ? 'text-red-500 bg-[#e1e1e1] dark:text-red-500 dark:bg-[#282828]' 
                              : 'text-gray-500 hover:text-[#0A0A0A] hover:bg-[#e7e7e7] dark:text-[#999999] dark:hover:text-white dark:hover:bg-[#282828]'
                        }`, isRecording)}
                    onClick={micDisabled && !isRecording ? undefined : toggleVoiceRecording}
                    type="button"
                    disabled={micDisabled && !isRecording}
                    style={isRecording ? {} : getControlButtonStyle()}
                  >
                    <MicrophoneIcon className="w-5 h-5 [@media(max-height:800px)]:w-4 [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:w-3.5 [@media(max-height:700px)]:h-3.5" />
                  </button>

                  {/* Microphone button tooltip */}
                  {!controlsDisabled && !isRecording && (
                    <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform z-[110]`}>
                      <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                        {voiceTranscribing ? 'Transcribing...' : isRecording ? 'Stop recording' : 'Start recording'}
                        <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Send button - becomes stop when recording */}
                <div className="relative group">
                  <button
                    onClick={isRecording ? toggleVoiceRecording : (disabled && !isInitialChatLoad ? (cancelResponse || (() => console.log('No cancel function available'))) : !disabled ? handleSubmit : undefined)}
                    className={`${(disabled && !isInitialChatLoad) || isRecording ? 'p-1.5' : 'p-2'} rounded-full transition-colors [@media(max-height:800px)]:p-1.5 [@media(max-height:700px)]:p-1 ${
                      controlsDisabled && !isRecording
                        ? 'cursor-not-allowed'
                        : (disabled && !isInitialChatLoad) || isRecording
                          ? 'hover:opacity-80 cursor-pointer'
                          : (!input.trim() && selectedFiles.length === 0)
                            ? 'cursor-not-allowed'
                            : 'hover:opacity-80 cursor-pointer'
                    }`}
                    style={{
                      backgroundColor: getSendButtonBackgroundColor(),
                      color: getSendButtonTextColor(),
                      opacity: (controlsDisabled && !isRecording) || (disabled && isInitialChatLoad)
                        ? 0.6 
                        : (!input.trim() && selectedFiles.length === 0 && !disabled && !isRecording) 
                          ? 0.5 
                          : 1
                    }}
                    type="button"
                    disabled={(controlsDisabled && !isRecording) || (disabled && isInitialChatLoad) || (!disabled && !isRecording && !input.trim() && selectedFiles.length === 0)}
                  >
                    {(() => {
                      if (isRecording || (disabled && !isInitialChatLoad)) {
                        return <StopIcon className="w-5 h-5 [@media(max-height:800px)]:w-3 [@media(max-height:800px)]:h-3 [@media(max-height:700px)]:w-2.5 [@media(max-height:700px)]:h-2.5" />;
                      }
                      return <FaArrowUp className="w-4 h-4 [@media(max-height:800px)]:w-3 [@media(max-height:800px)]:h-3 [@media(max-height:700px)]:w-2.5 [@media(max-height:700px)]:h-2.5" />;
                    })()}
                  </button>

                  {/* Send button tooltip */}
                  {!controlsDisabled && (
                    <div className={`absolute left-1/2 -translate-x-1/2 ${showTooltipsAbove ? 'bottom-[calc(100%+8px)] group-hover:-translate-y-1' : 'top-[calc(100%+12px)] group-hover:translate-y-1'} opacity-0 hidden md:block transition-all duration-200 pointer-events-none transform ${(input.trim() || selectedFiles.length > 0 || disabled || isRecording) && 'group-hover:opacity-100'}`}>
                      <div className="chat-input-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg [@media(max-height:800px)]:text-[12px] [@media(max-height:700px)]:text-[11px]">
                        {isRecording ? "Stop speaking" : (disabled && !isInitialChatLoad) ? "Stop response" : hasPaymentFailure ? (paymentData?.isSubscriptionInactive ? "No active subscription" : "Payment required to continue") : isUsageBlocked ? "Monthly usage limit reached" : "Send message"}
                        <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] transform rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] ${showTooltipsAbove ? '-bottom-[6px] rounded-br-[4px] border-b border-r' : '-top-[6px] rounded-tl-[4px] border-t border-l'}`}></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
            </div>
        </form>

        {/* Connect your tools bar - separate element, appears connected below input */}
        {!hasMessages && !chatId && (
          <ConnectYourToolsBar
            appearsConnected
            borderRadius={getDynamicBorderRadius(containerHeight)}
          />
        )}

        {/* Quick action buttons - shown when empty chat (hidden for now, add back later) */}
        {false && !hasMessages && !chatId && (
          <ChatQuickActions
            onActionClick={(prompt, action) => {
              if (action?.id === 'more') {
                toggleToolsDropdown();
              } else if (prompt) {
                setInput(prompt + ' ');
                setTimeout(() => textareaRef.current?.focus(), 0);
              }
            }}
            disabled={controlsDisabled}
          />
        )}
      </div>
      
      {/* Google Drive Modal */}
      <GoogleDriveModal
        isOpen={isGoogleDriveModalOpen}
        onClose={() => setIsGoogleDriveModalOpen(false)}
        onDocumentSelect={handleGoogleDriveDocumentSelect}
      />

      {/* Connect Tools Modal - shown when user selects Gmail/Drive/Calendar but hasn't connected */}
      <ConnectToolsModal
        isOpen={showConnectToolsModal}
        onClose={handleConnectToolsModalClose}
        darkMode={darkMode}
      />

    </div>
  );
};

export default ChatInput;